import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { disputeService } from '../services/disputeService';
import { procurementService } from '../services/api';
import { fmtDateTime, fmtCurrency, fmtRelativeTime, getOrgaoDisplay, getModoDisputaBadge, getFinalRankingRows, isModoAbertoFechado, computeIpcomOutcome, resolveModalidade, parseDecimalBr, descontoEquivalente } from '../utils/formatters';
import ComprasNetHealthDot from './ComprasNetHealthDot';
import { CartaoDoTopo } from './shared/lista/PecasDaLista';
import { secaoDaSessao, inicioDaSessao, vitoriaConfirmada, valorGanho, estadoDoCartao, propostasSemDisputa, contagem,
  resumoDasFinalizadas, lerFinalizadasAbertas, gravarFinalizadasAbertas, JANELA_PROXIMAS_H, DIAS_DAS_FINALIZADAS_RECENTES,
  EstadoDoCartao, SecaoDaDisputa } from '../utils/disputaLista';
import EncerrarSessoesPresas from './licitacoes/EncerrarSessoesPresas';
import { DisputeRankingDrawer } from './DisputeRankingDrawer';
import { DisputeExpandedHeader } from './DisputeExpandedHeader';
import { DisputeRankingInline } from './DisputeRankingInline';
import DesfechoPicker from './licitacoes/DesfechoPicker';
import { DisputeMessagesInline } from './DisputeMessagesInline';
import { DisputeItemCard } from './DisputeItemCard';
import { DisputeSessionCard } from './DisputeSessionCard';
import { LanceFechadoModal } from './LanceFechadoModal';
import { chaveUnidade, ehPcpValorGlobal, valorGlobalDaUnidade } from '../utils/unidadeDisputa';
import { useConfirm, usePedirTexto } from './shared/ConfirmDialog';
import { ehLanceReal, sessaoViva, confirmacaoDeIniciarReal, confirmacaoDeParar, pisosQueMudaram, confirmacaoDosPisos } from '../utils/disputaEscrita';
import { TONE_BADGE } from '../utils/statusColors';
import { confirmacaoDoSalvarDaDisputa } from '../utils/salvarDaDisputa';
import { PORTAL_MAP } from './licitacoes/procurementUi';
import { campo, campoComEstado } from '../design';
// NOTA (24/05/2026): imports MonitoringView/LicitacoesTab removidos junto com
// as tabs internas Cadastrar/Disputar/Monitorar — agora cada view é acessada
// só pelo menu lateral (decisão "menu flat", ver session-2026-05-23-licitacoes-cleanup-bll).

interface DisputeViewProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
  onNavigate?: (section: string) => void;
}

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    plus: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>,
    play: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    stop: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" /></svg>,
    settings: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    trash: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
    refresh: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
    x: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>,
    target: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} /></svg>,
    chevronDown: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>,
    chevronRight: <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>,
  };
  return icons[name] || null;
};

const STATUS_BADGES: Record<string, { label: string; color: string }> = {
  configurando: { label: 'Configurando', color: TONE_BADGE.warning },
  aguardando: { label: 'Aguardando', color: TONE_BADGE.progress },
  ativo: { label: 'Ativo', color: TONE_BADGE.live },
  pausado: { label: 'Pausado', color: TONE_BADGE.warning },
  finalizado: { label: 'Finalizado', color: TONE_BADGE.neutral },
  erro: { label: 'Erro', color: TONE_BADGE.danger },
};

// Pregão Aberto-Fechado tem 3 fases distintas — exibidas como sub-badge
const PHASE_BADGES: Record<string, { label: string; color: string }> = {
  aberta: { label: 'Aberta', color: TONE_BADGE.progress },
  aleatoria: { label: 'Aleatória', color: TONE_BADGE.live },
  fechada: { label: 'Fechada', color: TONE_BADGE.info },
  encerrada: { label: 'Encerrada', color: TONE_BADGE.neutral },
};

/** Deriva resultado da disputa pra IPCOM e status agregado dos grupos.
 * Usado pra mostrar badges na coluna Status do main row. */
export function getSessionOutcome(session: any): {
  ipcom: { label: string; color: string; title?: string } | null;
  groupStatuses: Array<{ identificador: string; situacao: string; color: string }>;
  compraEncerrada: boolean;
  resultadoPendente: boolean;
} {
  const items = Array.isArray(session?.itemLimits) ? session.itemLimits : [];

  // Status agregado dos grupos (1 entrada por grupo único)
  const groupMap = new Map<string, string>();
  for (const it of items) {
    if (it?.groupIdentificador && it.groupSituacao && !groupMap.has(it.groupIdentificador)) {
      groupMap.set(it.groupIdentificador, it.groupSituacao);
    }
  }
  // §4: tom semântico da fonte única. 'Julgado e habilitado' era LARANJA, que não
  // existe na paleta; virou `warning` por decisão de 24/08 — o sinal continua sendo
  // "atenção, ainda não é o desfecho", agora na cor que o resto do HUB usa para isso.
  const groupColorFor = (s: string) => s === 'Homologado'
    ? TONE_BADGE.success
    : s === 'Julgado e habilitado'
    ? TONE_BADGE.warning
    : TONE_BADGE.neutral;
  const groupStatuses = Array.from(groupMap.entries()).map(([identificador, situacao]) => ({
    identificador, situacao, color: groupColorFor(situacao),
  }));
  // Compra encerrada: 2 caminhos.
  //   1) Licitação POR GRUPO: todos os grupos com situação "Homologado".
  //   2) Licitação POR ITEM (sem grupo): qualquer proposta nos itens com
  //      situação Adjudicada/Homologada/Encerrada — sinal de que o portal
  //      saiu da fase de aceitação ("Aceita") e a compra está finalizada.
  //      Cobre Dispensa item-único e Pregão clássico, onde groupMap fica vazio.
  const HOMOLOGATED_LIKE = new Set(['Adjudicada', 'Homologada', 'Encerrada']);
  const compraEncerradaPorGrupo =
    groupMap.size > 0 && Array.from(groupMap.values()).every((v) => v === 'Homologado');
  const compraEncerradaPorItem =
    groupMap.size === 0 &&
    items.length > 0 &&
    items.some((it: any) => {
      const props: any[] = Array.isArray(it.allProposals) ? it.allProposals : [];
      return props.some((p: any) => p?.situacao && HOMOLOGATED_LIKE.has(p.situacao));
    });
  const compraEncerrada = compraEncerradaPorGrupo || compraEncerradaPorItem;

  // Resultado IPCOM. Calcula "rank efetivo" ignorando desclassificados:
  // posição na lista filtrada de propostas válidas. Sem isso, IPCOM em rank=2
  // com rank=1 desclassificado seria mostrado como "Não venceu — 2º" mas
  // tecnicamente é o vencedor (próximo após desclassificado).
  type Outcome = { rank: number | null; effectiveRank: number | null; desclassificada: boolean };
  const itemOutcomes: Outcome[] = items.map((it: any) => {
    const props: any[] = Array.isArray(it.allProposals) ? it.allProposals : [];
    const me = props.find((p: any) => p.ehMinha);
    if (!me) return { rank: null, effectiveRank: null, desclassificada: false };
    if (me.desclassificada) return { rank: me.rank ?? null, effectiveRank: null, desclassificada: true };
    // Rank efetivo: posição entre os não-desclassificados, ordenado por rank original
    const valid = props.filter((p: any) => !p.desclassificada);
    const idx = valid.findIndex((p: any) => p.ehMinha);
    return { rank: me.rank ?? null, effectiveRank: idx >= 0 ? idx + 1 : null, desclassificada: false };
  });
  const myEntries = itemOutcomes.filter((o) => o.rank !== null);
  let ipcom: { label: string; color: string; title?: string } | null = null;
  if (myEntries.length > 0) {
    const allDesclassif = myEntries.every((m) => m.desclassificada);
    const allFirstEffective = myEntries.every((m) => !m.desclassificada && m.effectiveRank === 1);
    if (allDesclassif) {
      ipcom = { label: 'Desclassificado', color: TONE_BADGE.danger, title: 'IPCOM foi desclassificado' };
    } else if (allFirstEffective) {
      ipcom = { label: 'Vencedor', color: TONE_BADGE.success, title: 'IPCOM em 1º lugar (após desclassificações)' };
    } else {
      // DISPUTA POR ITEM: ganhou uns, perdeu outros. "Não venceu — 2º/1º"
      // contradiz a realidade (venceu o item onde é 1º). Se há vitórias
      // parciais, mostra "Ganhou X de N". (Em grupo o rank é igual em todos os
      // itens → cai no allFirstEffective ou won=0; só por-item misto chega aqui.)
      const won = myEntries.filter((m) => !m.desclassificada && m.effectiveRank === 1).length;
      const n = myEntries.length;
      const porItemTitle = `IPCOM por item: ${myEntries.map((m) => m.desclassificada ? 'desclassif.' : (m.effectiveRank ? `${m.effectiveRank}º` : '—')).join(', ')}`;
      if (won > 0) {
        ipcom = {
          label: `Ganhou ${won} de ${n} ${n === 1 ? 'item' : 'itens'}`,
          color: TONE_BADGE.success,
          title: porItemTitle,
        };
      } else {
        const effRanks = myEntries.map((m) => m.effectiveRank).filter(Number.isFinite) as number[];
        const uniq = Array.from(new Set(effRanks));
        const display = uniq.slice(0, 2).map((r) => `${r}º`).join('/') + (uniq.length > 2 ? '…' : '');
        ipcom = {
          label: `Não venceu — ${display}`,
          color: TONE_BADGE.warning,
          title: porItemTitle,
        };
      }
    }
  }
  // Fallback canônico: quando `itemLimits[].allProposals` não foi populado
  // (Pregão por grupo com ranking só em todasPropostas, ou BLL/PCP), deriva o
  // outcome do ranking canônico via computeIpcomOutcome — mesma fonte do header
  // e do card. Sem isto, a DEFENSORIA (não convocada) ficava sem badge.
  if (!ipcom) {
    const oc = computeIpcomOutcome(session?.rankingData);
    if (oc.found && oc.classif != null) {
      ipcom = oc.isWinner
        ? { label: 'Vencedor', color: TONE_BADGE.success, title: 'IPCOM em 1º lugar' }
        : {
            label: `Não venceu — ${oc.classif}º`,
            color: TONE_BADGE.warning,
            title: `IPCOM em ${oc.classif}º de ${oc.total}${oc.derived ? ' (posição derivada do valor — não convocada para a fase fechada)' : ''}`,
          };
    }
  }
  // RESULTADO PENDENTE (28/08/2026, Dispensa 18/2026 MAPA): `finalizado` NÃO
  // quer dizer "resultado coletado". Quando o pregoeiro ainda não abriu a
  // Seleção de Fornecedores, o ranking final não existe no portal — o cron de
  // encerramento tenta, falha, e o backstop de 90min marca `finalizado` com
  // `resultData` NULL mesmo assim (é o comportamento certo: a alternativa era
  // deixar a sessão `ativo` pra sempre). Sem selo, essa sessão fica visualmente
  // idêntica a uma encerrada COM desfecho e ninguém volta pra coletar — foi o
  // que quase deixou a dispensa do MAPA sem resultado registrado.
  // `ipcom === null` é o mesmo sinal que o card já usa pra não desenhar badge de
  // resultado: as duas fontes (allProposals dos itemLimits e o ranking canônico
  // via computeIpcomOutcome) foram tentadas acima e nenhuma achou a IPCOM.
  const resultadoPendente = session?.status === 'finalizado' && !ipcom;
  return { ipcom, groupStatuses, compraEncerrada, resultadoPendente };
}

// Phase + convocação + outcome → label de auxílio na coluna Status
function getPhaseInfo(session: any): { phase: string | null; convocado: boolean | null; outcome: string | null } {
  const items = Array.isArray(session?.itemLimits) ? session.itemLimits : [];
  // Quando todos itens compartilham mesma fase, exibe-a; senão, usa o "mais avançado"
  const phases = items.map((i: any) => i?.phase).filter(Boolean);
  const convs = items.map((i: any) => i?.convocado).filter((v: any) => v === true || v === false);
  const phase = phases.length > 0 ? (phases.find((p: string) => p === 'encerrada') || phases.find((p: string) => p === 'fechada') || phases.find((p: string) => p === 'aleatoria') || phases[0]) : null;
  // convocado: se algum item é true → true; se todos são false → false; se misto → null
  let convocado: boolean | null = null;
  if (convs.length > 0) {
    if (convs.every((c: boolean) => c === true)) convocado = true;
    else if (convs.every((c: boolean) => c === false)) convocado = false;
  }
  const outcome = session?.resultData?.outcome || null;
  return { phase, convocado, outcome };
}

// PORTAL_MAP vem do procurementUi (fonte única, ~70 portais). Esta tela tinha a
// própria cópia parcial, e o efeito era o mesmo portal aparecer com badge aqui e
// slug cru na tela vizinha — dívida F-5 da auditoria de 13/08.


/** Roda `onMontar` uma vez, depois do render (para o "Criar disputa" pré-selecionar o item no modal). */
const AoMontar: React.FC<{ onMontar: () => void }> = ({ onMontar }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onMontar(); }, []);
  return null;
};

const DisputeView: React.FC<DisputeViewProps> = ({ showToast, onNavigate }) => {
  // Confirmação padrão do HUB (§8). O nativo ficou aqui até 23/08/2026 —
  // inclusive no botão de LANCE REAL, a confirmação mais crítica do módulo,
  // onde a consequência ("lance enviado não tem rollback") estava espremida
  // num `\n\n` de caixa branca do sistema operacional.
  const confirm = useConfirm();
  // P4 (12/10/2026): os pedidos nativos do param1 viram o pedido de texto do HUB.
  const pedirTexto = usePedirTexto();
  const pedirParam1 = (previo = '') => pedirTexto({
    title: 'Não consegui achar o link automaticamente',
    message: 'Cole o param1 da URL /BatchList?param1=... do portal.',
    label: 'param1',
    obrigatorio: true,
    valorInicial: previo,
    confirmLabel: 'Usar este param1',
  });
  const [sessions, setSessions] = useState<any[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [bidLogs, setBidLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configSessionId, setConfigSessionId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'items' | 'operations' | 'resultado' | 'ranking' | 'messages'>('items');
  // R1.14: busca compartilhada (manteve a infra de globalSearch pra uso interno do DisputeView)
  const [globalSearch, setGlobalSearch] = useState('');
  // L7 (O1/L1, 24/10/2026): as propostas, para achar a sessão marcada SEM disputa; e as Finalizadas recolhidas.
  const [propostas, setPropostas] = useState<any[]>([]);
  const [finalizadasAbertas, setFinalizadasAbertas] = useState<boolean>(() => lerFinalizadasAbertas());
  const [mostrarAntigas, setMostrarAntigas] = useState(false);
  const [buscaFinalizadas, setBuscaFinalizadas] = useState('');
  const [preNovaDisputa, setPreNovaDisputa] = useState<number | null>(null);
  const [expandedItemNumber, setExpandedItemNumber] = useState<number | null>(null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [refreshingRanking, setRefreshingRanking] = useState(false);
  // R1.5: drawer lateral de lances/propostas/concorrentes por item (ou grupo agregado se itemKey=-1)
  const [drawerItem, setDrawerItem] = useState<{ sessionId: number; itemKey: number | -1; description?: string } | null>(null);

  // Modal de Lance Final Fechado (Pregão Aberto/Fechado). Aciona quando alguma
  // sessão ativa tem item com fase='LF' e convocado=true. dismissed* rastreia
  // sessões já respondidas/canceladas pra não reabrir em loop.
  const [lfModalSession, setLfModalSession] = useState<any | null>(null);
  const lfDismissedRef = useRef<Set<number>>(new Set());

  // New dispute form
  const [items, setItems] = useState<any[]>([]);
  const [portals, setPortals] = useState<any[]>([]);
  const [newForm, setNewForm] = useState({ itemId: '', portalId: '', startTime: '', endTime: '' });
  // True quando startTime/endTime foram derivados de dataEncerramento (e não
  // lidos de item.dataSessaoInicio/Fim) — usado pra mostrar aviso no modal de
  // que o horário é estimativa e pode estar 1 min off da janela real.
  const [datesDerived, setDatesDerived] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [itemDropdownOpen, setItemDropdownOpen] = useState(false);
  const [newMode, setNewMode] = useState<'select' | 'manual'>('select');
  const [manualForm, setManualForm] = useState({ portal: 'comprasnet', codigoUnidade: '', numeroCompra: '', modalidade: 'de' });
  const [manualLoading, setManualLoading] = useState(false);

  // Polling
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const configSession = sessions.find((s) => s.id === configSessionId) || null;
  const expandedSession = sessions.find((s) => s.id === expandedId) || null;

  // ========== Metrics ==========


  const filteredSessions = useMemo(() => {
    let list = sessions;
    // R1.14: filtro global compartilhado entre abas — busca em órgão, número, UASG, objeto
    if (globalSearch.trim()) {
      const q = globalSearch.toLowerCase();
      list = list.filter((s: any) => {
        const item = s.item || {};
        return (
          (item.orgaoNome || '').toLowerCase().includes(q) ||
          (item.unidadeNome || '').toLowerCase().includes(q) ||
          (item.objetoCompra || '').toLowerCase().includes(q) ||
          (item.numeroCompra || '').toString().includes(q) ||
          (item.codigoUnidade || '').toString().includes(q) ||
          (s.proposal?.title || '').toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [sessions, globalSearch]);

  /** E1/V1: o estado único de cada sessão, e a vitória confirmada (o resumo e o cartão usam a mesma regra). */
  const estadoDaSessao = useCallback((session: any, agora: number): { estado: EstadoDoCartao; secao: SecaoDaDisputa; vitoria: boolean } => {
    const secao = secaoDaSessao(session, agora);
    const oc = computeIpcomOutcome(session?.rankingData);
    const ipcomEm1o = !!(oc.found && oc.isWinner);
    const vitoria = vitoriaConfirmada(session, ipcomEm1o);
    const { ipcom, compraEncerrada, resultadoPendente } = getSessionOutcome(session);
    // O convocado HISTÓRICO, como o selo do cartão: a flag `convocado` é viva e o encerramento a zera, então sozinha ela
    // dizia "não convocado" para toda sessão de AF terminada (o "Não convocado mentia", 14/09/2026). Vale o marcador
    // `melhorAberturaAF`, gravado uma vez na convocação; o negativo só quando a sessão é nova o bastante para tê-lo.
    const fase = getPhaseInfo(session);
    const lims: any[] = Array.isArray(session?.itemLimits) ? session.itemLimits : [];
    const terminou = fase.phase === 'fechada' || fase.phase === 'encerrada';
    const convocado: boolean | null = !terminou ? null
      : lims.some((l: any) => l?.melhorAberturaAF != null) ? true
      : lims.some((l: any) => l?.melhorItem != null) && fase.convocado === false ? false
      : null;
    const estado = estadoDoCartao({
      secao, agora, status: String(session?.status || ''), inicio: inicioDaSessao(session), vitoria,
      classif: oc.found ? oc.classif : null, ipcomEm1o, convocado, desclassificado: ipcom?.label === 'Desclassificado',
      resultadoPendente: !!resultadoPendente, compraEncerrada: !!compraEncerrada,
    });
    return { estado, secao, vitoria };
  }, []);

  // ========== Load Data ==========

  const loadSessions = useCallback(async () => {
    try {
      const data = await disputeService.listSessions();
      setSessions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load dispute sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBidLogs = useCallback(async (sessionId: number) => {
    try {
      const data = await disputeService.getBidLogs(sessionId);
      setBidLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load bid logs:', err);
    }
  }, []);

  const loadFormData = useCallback(async () => {
    try {
      const [itemsData, portalsData] = await Promise.all([
        procurementService.getItems({ statusNot: 'descartado', limit: 200, sortBy: 'dataEncerramento', sortOrder: 'DESC' }),
        procurementService.getPortals(),
      ]);
      const allItems = Array.isArray(itemsData) ? itemsData : itemsData?.data || [];
      // Exclude items that already have an active dispute session
      const activeItemIds = new Set(sessions.filter((s: any) => !['finalizado', 'erro'].includes(s.status)).map((s: any) => s.itemId));
      setItems(allItems.filter((it: any) => !activeItemIds.has(it.id)));
      setPortals(Array.isArray(portalsData) ? portalsData : []);
    } catch {}
  }, [sessions]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const [propostasFalharam, setPropostasFalharam] = useState(false);
  const loadPropostas = useCallback(async () => {
    try {
      const data = await procurementService.getProposals({});
      setPropostas(Array.isArray(data) ? data : []);
      setPropostasFalharam(false);
    } catch (e) {
      // Sem a lista, a seção "Próximas" mostra só as disputas existentes; o resumo diz "—" em "Sem disputa configurada".
      setPropostas([]);
      setPropostasFalharam(true);
    }
  }, []);
  useEffect(() => { loadPropostas(); }, [loadPropostas]);
  useEffect(() => { if (!showNewModal) setPreNovaDisputa(null); }, [showNewModal]);

  // Polling for active sessions
  useEffect(() => {
    const hasActive = sessions.some((s) => ['ativo', 'aguardando'].includes(s.status));
    if (hasActive) {
      pollingRef.current = setInterval(() => {
        loadSessions();
        // Onda 2 (F-2): os logs do robô atualizam DURANTE a disputa. Antes só
        // carregavam ao expandir o card — lance/recusa novos exigiam recolher
        // e re-expandir pra aparecer.
        if (expandedId) loadBidLogs(expandedId);
      }, 3000);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [sessions, loadSessions, expandedId, loadBidLogs]);

  useEffect(() => {
    if (expandedId) loadBidLogs(expandedId);
  }, [expandedId, loadBidLogs]);

  // Detecta convocação para Fase Fechada (Pregão Aberto/Fechado) e dispara
  // modal automaticamente. Critério: sessão ativa + algum item com convocado=true
  // E fase=LF/AF (LF=fechada já aberta, AF=aguardando fase fechada). Não reabre
  // sessões que o user já dismissou (mantém ref pra não perder no re-render).
  useEffect(() => {
    if (lfModalSession) return; // já tem modal aberto
    for (const s of sessions) {
      if (!['ativo', 'aguardando'].includes(s.status)) continue;
      if (lfDismissedRef.current.has(s.id)) continue;
      const r = s.rankingData;
      const igRaw = r?.itensGrupo?.['-1'] || r?.emDisputa;
      const rows: any[] = Array.isArray(igRaw)
        ? igRaw
        : igRaw?.registros || igRaw?.itens || igRaw?.items || [];
      const convocadoEmLF = rows.some(
        (row: any) => row?.convocado === true && (row?.fase === 'LF' || row?.fase === 'AF'),
      );
      if (convocadoEmLF) {
        setLfModalSession(s);
        break;
      }
    }
  }, [sessions, lfModalSession]);

  // ========== Actions ==========

  const handleCreate = async () => {
    if (newMode === 'manual') {
      if (!manualForm.codigoUnidade || !manualForm.numeroCompra || !newForm.portalId) {
        showToast('Preencha UASG, número da compra e portal', 'error');
        return;
      }
      setManualLoading(true);
      try {
        // Register manual → auto-creates item + proposal
        const item = await procurementService.registerManual({
          portal: manualForm.portal,
          codigoUnidade: manualForm.codigoUnidade,
          numeroCompra: manualForm.numeroCompra,
          modalidade: manualForm.modalidade,
        });
        // Create dispute session with the new item
        await disputeService.createSession({
          itemId: item.id,
          portalId: Number(newForm.portalId),
          disputeStartTime: newForm.startTime || undefined,
          disputeEndTime: newForm.endTime || undefined,
        });
        showToast('Disputa criada com sucesso');
        setShowNewModal(false);
        setNewForm({ itemId: '', portalId: '', startTime: '', endTime: '' });
        setManualForm({ portal: 'comprasnet', codigoUnidade: '', numeroCompra: '', modalidade: 'de' });
        loadSessions();
      } catch (err: any) {
        showToast(err.response?.data?.message || 'Erro ao criar disputa', 'error');
      } finally {
        setManualLoading(false);
      }
      return;
    }

    if (!newForm.itemId || !newForm.portalId) {
      showToast('Selecione uma licitação e um portal', 'error');
      return;
    }
    try {
      await disputeService.createSession({
        itemId: Number(newForm.itemId),
        portalId: Number(newForm.portalId),
        disputeStartTime: newForm.startTime || undefined,
        disputeEndTime: newForm.endTime || undefined,
      });
      showToast('Disputa criada com sucesso');
      setShowNewModal(false);
      setNewForm({ itemId: '', portalId: '', startTime: '', endTime: '' });
      setItemSearch('');
      loadSessions();
      setTimeout(loadSessions, 20000); // Refresh after scrape completes
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao criar disputa', 'error');
    }
  };

  const handleStart = async (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // D3 (Heitor, 12/10/2026): iniciar em LANCE REAL pergunta, com portal, itens e soma dos pisos. Em simulação, não.
    const sessaoAIniciar = sessions.find((s: any) => s.id === id);
    if (sessaoAIniciar && ehLanceReal(sessaoAIniciar.bidConfig)) {
      const portal = sessaoAIniciar.portal?.portalName || PORTAL_MAP[sessaoAIniciar.item?.portalOrigem]?.label || sessaoAIniciar.item?.portalOrigem || 'portal';
      if (!(await confirm(confirmacaoDeIniciarReal(sessaoAIniciar, portal)))) return;
    }
    // R3 (BLL server-side): o backend auto-resolve o batchId via linkOrigem do
    // item (PNCP → ProcessView → token [gkz], sem login). Tenta sem prompt; só
    // pede o param1 manual se o auto-resolve falhar (item sem linkOrigem ou
    // layout BLL mudou). Mesmo padrão de handleBllCollectResult.
    const start = async (opts?: { bllBatchId?: string }) => {
      await disputeService.startDispute(id, opts);
      showToast('Disputa iniciada');
      loadSessions();
    };
    try {
      await start();
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || '';
      if (/param1|batchId|Sincronizar BLL|HTTP 3|HTTP 4|expirou|Sessão BLL/i.test(msg)) {
        const sess = sessions.find((s: any) => s.id === id);
        const previous = (sess?.externalSessionId as string | undefined) || '';
        const input = await pedirParam1(previous);
        if (!input) return; // cancelou
        try {
          await start({ bllBatchId: input.trim() });
        } catch (err2: any) {
          showToast(err2?.response?.data?.message || 'Erro ao iniciar disputa', 'error');
        }
      } else {
        showToast(msg || 'Erro ao iniciar disputa', 'error');
      }
    }
  };

  const handleStop = async (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // D3: parar com a disputa viva pergunta (o robô deixa de cobrir).
    const sessaoAParar = sessions.find((s: any) => s.id === id);
    if (sessaoViva(sessaoAParar?.status) && !(await confirm(confirmacaoDeParar()))) return;
    try {
      await disputeService.stopDispute(id);
      showToast('Disputa parada');
      loadSessions();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao parar disputa', 'error');
    }
  };

  const handleDelete = async (id: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // Aviso diferenciado conforme estado (Audit A3 27/05/26):
    // - configurando: avisa que perde itemLimits + estratégias sem recovery
    // - ativo/aguardando: avisa que tem disputa em andamento
    // - finalizado: aviso simples
    const sess = sessions.find((s) => s.id === id);
    let titulo = 'Remover esta disputa?';
    let consequencia: React.ReactNode = null;
    if (sess?.status === 'configurando') {
      titulo = 'Remover disputa em CONFIGURAÇÃO?';
      consequencia = 'Os pisos por item e as estratégias de lance se perdem — a sessão terá que ser recriada do zero.';
    } else if (sess?.status === 'ativo' || sess?.status === 'aguardando') {
      titulo = 'Remover disputa EM ANDAMENTO?';
      consequencia = 'O robô para de operar e a aba é fechada. Os lances já enviados ao portal PERMANECEM — não há rollback.';
    }
    if (!(await confirm({ title: titulo, consequence: consequencia, danger: true, confirmLabel: 'Remover' }))) return;
    try {
      await disputeService.deleteSession(id);
      showToast('Disputa removida');
      if (expandedId === id) setExpandedId(null);
      loadSessions();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao remover', 'error');
    }
  };

  /** Devolve se salvou: o ConfigModal só atualiza a sessão na tela quando a pessoa confirmou e o servidor gravou. */
  const handleSaveConfig = async (strategies?: any, bidConfig?: any, itemLimits?: any[], situacao?: { vaiLancarAgora?: boolean }): Promise<boolean> => {
    if (!configSession) return false;
    const limits = itemLimits || configSession.itemLimits;
    // UMA pergunta do HUB com as consequências que valem (14/10/2026): o "vai lançar AGORA" vinha num window.confirm
    // nativo ANTES deste modal, e o Heitor via as duas. Ver utils/salvarDaDisputa.
    // P2 (12/10/2026, #459): quando o piso muda, a pergunta lista antes → depois de cada item que muda (casa por itemNumber).
    const disputaEmCurso = configSession.status === 'ativo' || configSession.status === 'aguardando';
    const mudancas = itemLimits && disputaEmCurso ? pisosQueMudaram(configSession.itemLimits || [], itemLimits) : [];
    const pisoMudou = mudancas.length > 0;
    const pergunta = confirmacaoDoSalvarDaDisputa({ vaiLancarAgora: !!situacao?.vaiLancarAgora, pisoMudou });
    if (pergunta) {
      const ok = await confirm({
        title: pergunta.title,
        ...(pisoMudou ? { message: confirmacaoDosPisos(mudancas).message } : {}),
        consequence: pergunta.linhas.length === 1 ? pergunta.linhas[0] : (
          <ul className="list-disc pl-4 space-y-1">{pergunta.linhas.map((l) => <li key={l}>{l}</li>)}</ul>
        ),
        danger: true,
        confirmLabel: pergunta.confirmLabel,
      });
      if (!ok) return false;
    }
    try {
      await disputeService.updateStrategy(configSession.id, {
        strategies: strategies || configSession.strategies,
        bidConfig: bidConfig || configSession.bidConfig,
        itemLimits: limits,
      });
      showToast('Configurações salvas');
      setShowConfigModal(false);
      setConfigSessionId(null);
      loadSessions();
      return true;
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao salvar', 'error');
      return false;
    }
  };

  const handleRefreshRanking = async () => {
    if (!expandedSession) return;
    setRefreshingRanking(true);
    try {
      await disputeService.refreshRanking(expandedSession.id);
      showToast('Ranking atualizado');
      await loadSessions();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao atualizar ranking', 'error');
    } finally {
      setRefreshingRanking(false);
    }
  };

  // Coletar resultado de uma disputa Pausada/encerrada direto pelo card (sem
  // precisar expandir). Lê o ranking final do portal via refresh-ranking. (03/06)
  const [collectingId, setCollectingId] = useState<number | null>(null);
  // Coleta portal-aware (botão único no header). BLL tem caminho server-side
  // próprio (BatchList scrape, com fallback de prompt do batchId); ComprasNet/PCP
  // usam refresh-ranking. Padroniza os 3 portais num só botão. (03/06/26)
  const handleCollect = async (id: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const sess = sessions.find((s) => s.id === id);
    // BLL e BNC = mesma plataforma (base gkz): caminho server-side próprio.
    const portalRaw = String(sess?.portal?.portalName || '').toLowerCase();
    const isBncSess = portalRaw.includes('bnc');
    const isGkzSess = portalRaw.includes('bll') || isBncSess;
    const tag = isBncSess ? 'BNC' : 'BLL';
    const collectGkz = (opts?: { bllBatchId?: string }) =>
      isBncSess ? disputeService.bncCollectResult(id, opts) : disputeService.bllCollectResult(id, opts);
    setCollectingId(id);
    try {
      if (isGkzSess) {
        const result = await collectGkz();
        if (result?.ok === false) {
          showToast(result?.message || `${tag} ainda não chegou na fase de coleta`, 'error');
        } else {
          const n = result?.ranking?.length ?? 0;
          showToast(`Ranking ${tag} coletado (${n} fornecedor${n === 1 ? '' : 'es'})`);
          await loadSessions();
        }
      } else {
        await disputeService.refreshRanking(id);
        showToast('Resultado coletado do portal');
        await loadSessions();
      }
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || '';
      // BLL/BNC: backend pede o param1 quando não auto-resolve o batchId → prompt.
      if (isGkzSess && /param1|batchId|Sincronizar B(LL|NC)/i.test(msg)) {
        const input = await pedirParam1();
        if (input) {
          try {
            const r = await collectGkz({ bllBatchId: input.trim() });
            const n = r?.ranking?.length ?? 0;
            showToast(`Ranking ${tag} coletado (${n} fornecedor${n === 1 ? '' : 'es'})`);
            await loadSessions();
          } catch (e2: any) {
            showToast(e2?.response?.data?.message || `Erro ao coletar resultado ${tag}`, 'error');
          }
        }
      } else {
        showToast(msg || 'Erro ao coletar resultado', 'error');
      }
    } finally {
      setCollectingId(null);
    }
  };

  // BLL: coleta server-side via HTTP scrape — caminho separado do ComprasNet.
  // Funciona mesmo se a sessão nunca foi iniciada (sem Play), porque o backend
  // chama /BatchList/PartialUnique?idStatus=16 → /participantRanking direto.
  const [collectingBll, setCollectingBll] = useState(false);
  const handleBllCollectResult = async () => {
    if (!expandedSession) return;
    setCollectingBll(true);
    // BLL e BNC = mesma plataforma (base gkz). Backend auto-resolve o batchId via
    // linkOrigem; só pede prompt se falhar.
    const isBncSess = /bnc/i.test(String(expandedSession?.portal?.portalName || ''));
    const tag = isBncSess ? 'BNC' : 'BLL';
    const tryCollect = async (opts?: { bllBatchId?: string }) => {
      const result = isBncSess
        ? await disputeService.bncCollectResult(expandedSession.id, opts)
        : await disputeService.bllCollectResult(expandedSession.id, opts);
      if (result?.ok === false) {
        showToast(result?.message || `${tag} ainda não chegou na fase HABILITAÇÃO`, 'error');
      } else {
        const n = result?.ranking?.length ?? 0;
        showToast(`Ranking ${tag} coletado (${n} fornecedor${n === 1 ? '' : 'es'})`);
        await loadSessions();
      }
    };
    try {
      await tryCollect();
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || '';
      if (/param1|batchId|Sincronizar B(LL|NC)/i.test(msg)) {
        const input = await pedirParam1();
        if (input) {
          try {
            await tryCollect({ bllBatchId: input.trim() });
          } catch (err2: any) {
            showToast(err2?.response?.data?.message || `Erro ao coletar resultado ${tag}`, 'error');
          }
        }
      } else {
        showToast(msg || `Erro ao coletar resultado ${tag}`, 'error');
      }
    } finally {
      setCollectingBll(false);
    }
  };

  const [finalizingOutcome, setFinalizingOutcome] = useState(false);
  const handleFinalizeOutcome = async () => {
    if (!expandedSession?.item?.id) return;
    if (!(await confirm({
      title: 'Finalizar este item pelo ranking atual?',
      consequence: 'A IPCOM será marcada como ganho ou perdido conforme a posição no ranking coletado agora.',
      confirmLabel: 'Finalizar',
    }))) return;
    setFinalizingOutcome(true);
    try {
      const result = await procurementService.finalizeItemByRanking(expandedSession.item.id);
      if (result.finalized > 0) {
        const detail = result.details?.[0];
        showToast(`Item finalizado: ${detail?.outcome || 'atualizado'}`);
      } else {
        showToast(result.details?.[0]?.reason || 'Não foi possível finalizar — ranking incompleto', 'error');
      }
      await loadSessions();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Erro ao finalizar item', 'error');
    } finally {
      setFinalizingOutcome(false);
    }
  };

  const toggleExpand = (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      setActiveTab('items');
    }
  };

  /** O cartão de uma sessão, com o painel expandido (o mesmo de antes; L7 só acrescenta o estado e a seção). */
  const renderSessao = (session: any, estado?: EstadoDoCartao, secao?: SecaoDaDisputa) => {
            const badge = STATUS_BADGES[session.status] || STATUS_BADGES.configurando;
            const item = session.item;
            const isExpanded = expandedId === session.id;
            // Identifica portal por: 1) session.portal.portalName (autoritativo),
            // 2) fallback item.portalOrigem. Pra BLL especificamente o
            // portalOrigem pode estar vazio mas portal.portalName='BLL Compras'.
            const portalNameRaw: string = String(session.portal?.portalName || '').toLowerCase();
            const portalKey: string | null =
              portalNameRaw.includes('bll') ? 'bll'
              : portalNameRaw.includes('bnc') ? 'bnc'
              : portalNameRaw.includes('pcp') || portalNameRaw.includes('portal de compras') ? 'pcp'
              : portalNameRaw.includes('caixa') ? 'caixa'
              : portalNameRaw.includes('compras.gov') || portalNameRaw.includes('comprasnet') ? 'comprasnet'
              : item?.portalOrigem || null;
            const phaseInfo = getPhaseInfo(session);
            const sessionOutcome = getSessionOutcome(session);
            return (
              <DisputeSessionCard
                key={session.id}
                estado={estado}
                secao={secao}
                session={session}
                isExpanded={isExpanded}
                onToggle={() => toggleExpand(session.id)}
                onStart={(e) => handleStart(session.id, e)}
                onStop={(e) => handleStop(session.id, e)}
                onConfig={(e) => { e.stopPropagation(); setConfigSessionId(session.id); setShowConfigModal(true); }}
                onCollect={(e) => handleCollect(session.id, e)}
                collecting={collectingId === session.id}
                onDelete={(e) => handleDelete(session.id, e)}
                statusBadge={badge}
                phaseInfo={phaseInfo}
                sessionOutcome={sessionOutcome}
                portalLabel={portalKey ? PORTAL_MAP[portalKey] : undefined}
                expandedContent={isExpanded && expandedSession && (
                  <div className="bg-central-cartao">
                            {/* Error message */}
                            {expandedSession.errorMessage && (
                              <div className="mx-4 mt-3 p-2 bg-red-900/20 border border-red-800 rounded text-sm text-red-400">
                                {expandedSession.errorMessage}
                              </div>
                            )}

                            {/* R1.6: Header com Fase Progress + 3 Stats (tempo, meu, melhor) */}
                            <div className="px-4 pt-4">
                              <DisputeExpandedHeader session={expandedSession} />
                            </div>

                            {/* Onda 2 (F-1): ÚLTIMA AÇÃO DO ROBÔ — o operador vê em <5s o que
                                o robô tentou e o que o portal respondeu (na sessão 80, ~150
                                recusas 422 ficaram invisíveis e o user achou que ele "não fez
                                nada"). Fonte: dispute_bid_logs (agora alimentado pelo R3). */}
                            {(() => {
                              const last = bidLogs[0];
                              if (!last || !['ativo', 'aguardando'].includes(expandedSession.status)) return null;
                              const t = last.timestamp ? new Date(last.timestamp) : null;
                              const hora = t ? t.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
                              const rejeitado = last.result === 'rejected' || last.result === 'error';
                              const rotulo = last.result === 'accepted' ? '✅ ACEITO'
                                : last.result === 'dry_run' ? '🧪 SIMULADO'
                                : last.result === 'queued' ? '📤 NA FILA'
                                : last.result === 'sent' ? '📤 ENVIADO'
                                : rejeitado ? '⛔ RECUSADO' : last.result;
                              const recusasSeguidas = (() => {
                                let n = 0;
                                for (const l of bidLogs) { if (l.result === 'rejected' || l.result === 'error') n++; else break; }
                                return n;
                              })();
                              return (
                                <div className={`mx-4 mt-3 px-3 py-2 rounded border text-sm flex flex-wrap items-center gap-x-3 gap-y-1 ${
                                  rejeitado ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-central-elevado border-central-borda text-slate-300'
                                }`}>
                                  <span className="font-medium text-slate-400">🤖 Última ação</span>
                                  <span className="font-mono">{hora}</span>
                                  <span>item {last.itemNumber}</span>
                                  <span className="font-mono">R$ {Number(last.bidValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                                  <span className={rejeitado ? 'font-semibold animate-pulse' : 'font-semibold'}>{rotulo}</span>
                                  {recusasSeguidas >= 3 && (
                                    <span className="font-semibold text-red-400">⚠ {recusasSeguidas} recusas seguidas</span>
                                  )}
                                  {last.errorMessage && (
                                    <span className="w-full text-xs text-red-400/90 truncate" title={last.errorMessage}>
                                      {last.errorMessage}
                                    </span>
                                  )}
                                  {last.strategy && !last.errorMessage && (
                                    <span className="w-full text-xs text-slate-500 truncate" title={last.strategy}>
                                      {last.strategy}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Tabs */}
                            <div className="flex border-b border-central-borda px-4">
                              <button
                                onClick={() => setActiveTab('items')}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                  activeTab === 'items'
                                    ? 'text-white border-b-2 border-white'
                                    : 'text-slate-400 hover:text-slate-300'
                                }`}
                              >
                                Itens ({(expandedSession.itemLimits || []).length})
                              </button>
                              {/* Onda 2 (F-2): aba SEMPRE visível — "Operações (0)" é informação
                                  (na sessão 80 o zero era o SINTOMA: ~150 tentativas sem registro
                                  e o operador concluiu que o robô não fez nada). */}
                              <button
                                onClick={() => setActiveTab('operations')}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                  activeTab === 'operations'
                                    ? 'text-white border-b-2 border-white'
                                    : 'text-slate-400 hover:text-slate-300'
                                }`}
                              >
                                Operações ({bidLogs.length})
                                {bidLogs.some((l: any) => l.result === 'rejected' || l.result === 'error') && (
                                  <span className="ml-1 text-red-400">●</span>
                                )}
                              </button>
                              {/* R1.7: Tab Resultado removida — Ranking (11 fornecedores) já contém a info final.
                                 Mantemos null pra preservar setActiveTab('resultado') em códigos legados. */}
                              {false && (() => {
                                const hasRanking = (expandedSession.itemLimits || []).some(
                                  (i: any) => Array.isArray(i.allProposals) && i.allProposals.length > 0
                                );
                                const showResultado = expandedSession.status === 'finalizado' || hasRanking;
                                if (!showResultado) return null;
                                return (
                                  <button
                                    onClick={() => setActiveTab('resultado')}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                                      activeTab === 'resultado'
                                        ? 'text-white border-b-2 border-white'
                                        : 'text-slate-400 hover:text-slate-300'
                                    }`}
                                  >
                                    Resultado
                                  </button>
                                );
                              })()}
                              {/* R1.6: tab Ranking inline (lê rankingData.todasPropostas).
                                  Helper resolve as 3 shapes do extension (-1 normalizado,
                                  -1 direto, ou per-item replicado em grupo). */}
                              {(() => {
                                const rows = getFinalRankingRows(expandedSession.rankingData);
                                // BLL/BNC multi-lote tem ranking próprio por lote em
                                // resultData.lotes — que o getFinalRankingRows (fonte
                                // ComprasNet) não enxerga. Sem isto a aba some numa
                                // sessão que TEM ranking, só que de outra fonte.
                                const lotes: any[] = Array.isArray(expandedSession?.resultData?.lotes)
                                  ? expandedSession.resultData.lotes
                                  : [];
                                const rowCount = rows.length;
                                if (rowCount === 0 && lotes.length === 0 && expandedSession.status !== 'finalizado') return null;
                                const rotulo = lotes.length > 1
                                  ? `${lotes.length} lotes`
                                  : (rowCount > 0 ? String(rowCount) : '');
                                return (
                                  <button
                                    onClick={() => setActiveTab('ranking')}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                                      activeTab === 'ranking'
                                        ? 'text-white border-b-2 border-white'
                                        : 'text-slate-400 hover:text-slate-300'
                                    }`}
                                  >
                                    Ranking {rotulo && <span className="text-xs text-slate-500">({rotulo})</span>}
                                  </button>
                                );
                              })()}
                              <button
                                onClick={() => setActiveTab('messages')}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${
                                  activeTab === 'messages'
                                    ? 'text-white border-b-2 border-white'
                                    : 'text-slate-400 hover:text-slate-300'
                                }`}
                              >
                                Mensagens
                              </button>
                            </div>

                            {/* Items Tab */}
                            {activeTab === 'items' && (() => {
                              const itemList = expandedSession.itemLimits || [];
                              const allItemNumbers = itemList
                                .filter((i: any) => Array.isArray(i.allProposals) && i.allProposals.length > 0)
                                .map((i: any) => i.itemNumber);
                              const allGroupKeys = Array.from(new Set(
                                itemList
                                  .filter((i: any) => i.groupIdentificador && Array.isArray(i.groupRanking) && i.groupRanking.length > 0)
                                  .map((i: any) => i.groupIdentificador)
                              )) as string[];
                              const isAllExpanded = expandedItemNumber === -1;
                              // Fallback: grupo capturado no Encerrado (todasPropostas via
                              // state-update da extensão) não seta allProposalsUpdatedAt —
                              // sem isto o header dizia "não coletado" com o ranking no banco.
                              const tpRk = expandedSession.rankingData?.todasPropostas || {};
                              const temTp = Object.keys(tpRk).some((k) => {
                                const e = (tpRk as any)[k];
                                const arr = Array.isArray(e) ? e : Array.isArray(e?.items) ? e.items : [];
                                return arr.length > 0;
                              });
                              const updatedAt = itemList
                                .map((i: any) => i.allProposalsUpdatedAt)
                                .filter(Boolean)
                                .sort()
                                .pop() || (temTp ? expandedSession.lastUpdate : undefined);
                              return (
                              <div className="overflow-x-auto">
                                <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-central-borda bg-central-cartao">
                                  <div className="text-xs text-slate-500 flex items-center gap-2">
                                    {updatedAt ? (
                                      <>
                                        <span>Ranking atualizado em {new Date(updatedAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                                        <span className="text-slate-600">•</span>
                                        <span className="text-slate-400">{fmtRelativeTime(updatedAt)}</span>
                                      </>
                                    ) : (
                                      <span>Ranking de propostas não coletado ainda</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {(allItemNumbers.length > 0 || allGroupKeys.length > 0) && (
                                      <button
                                        onClick={() => {
                                          if (isAllExpanded) {
                                            setExpandedItemNumber(null);
                                            setExpandedGroupKey(null);
                                          } else {
                                            setExpandedItemNumber(-1);
                                            setExpandedGroupKey('__ALL__');
                                          }
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1 text-xs rounded bg-slate-600/20 hover:bg-slate-600/30 text-slate-300 border border-slate-500/30 transition-colors"
                                        title={isAllExpanded ? 'Recolher todos os rankings' : 'Expandir todos os rankings'}
                                      >
                                        <Icon name={isAllExpanded ? 'chevronDown' : 'chevronRight'} className="w-3.5 h-3.5" />
                                        {isAllExpanded ? 'Recolher todos' : 'Expandir todos'}
                                      </button>
                                    )}
                                    {/* Coleta unificada agora no header do card (botão "Coletar resultado"
                                        portal-aware: BLL→bll-collect, ComprasNet/PCP→refresh-ranking).
                                        Removidos os botões duplicados "Coletar resultado (BLL)" e
                                        "Recoletar resultado" daqui. (03/06/26) */}
                                    {/* "Finalizar ganho/perdido" só aparece quando há ranking final pra basear a decisão. */}
                                    {getFinalRankingRows(expandedSession.rankingData).length > 0
                                      && expandedSession.status === 'finalizado' && (
                                      <button
                                        onClick={handleFinalizeOutcome}
                                        disabled={finalizingOutcome}
                                        className="flex items-center gap-1.5 px-3 py-1 text-xs rounded bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Registra o resultado no catálogo de licitações IPCOM (ganho/perdido baseado na posição final)"
                                      >
                                        <Icon name="check" className="w-3.5 h-3.5" />
                                        {finalizingOutcome ? 'Finalizando...' : 'Registrar resultado'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="relative">
                                  {refreshingRanking && (
                                    <div className="absolute inset-0 bg-central-elevado backdrop-blur-[1px] z-10 flex items-center justify-center pointer-events-none">
                                      <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-central-cartao/90 border border-blue-500/30 text-blue-300 text-xs shadow-lg">
                                        <Icon name="refresh" className="w-4 h-4 animate-spin" />
                                        Atualizando ranking…
                                      </div>
                                    </div>
                                  )}
                                <div className="space-y-2.5 p-3">
                                  {(expandedSession.itemLimits || []).map((itm: any, idx: number, arr: any[]) => {
                                    // Chave de grupo NORMALIZADA: usa groupNumber (consistente entre
                                    // todos os sub-itens) e só cai no groupIdentificador se não houver.
                                    // Antes agrupava só por groupIdentificador, que vinha inconsistente
                                    // (vias mistas de coleta: 5 itens sem, 1 com) → separava 1 item do
                                    // grupo errado (SEMU 90001 item 6). (03/06/26)
                                    const groupKey = (x: any): string | undefined =>
                                      x?.groupNumber != null ? String(x.groupNumber) : (x?.groupIdentificador || undefined);
                                    const grupoAtual = groupKey(itm);
                                    const grupoAnterior = idx > 0 ? groupKey(arr[idx - 1]) : undefined;
                                    const showGroupHeader = !!grupoAtual && grupoAtual !== grupoAnterior;
                                    const itensGrupoRaw = expandedSession.rankingData?.itensGrupo?.['-1'];
                                    const itensGrupoRows: any[] = Array.isArray(itensGrupoRaw)
                                      ? itensGrupoRaw
                                      : (itensGrupoRaw?.registros || itensGrupoRaw?.itens || itensGrupoRaw?.items || []);
                                    const itensGrupoRow = itensGrupoRows.find((r: any) => r.numero === itm.itemNumber);
                                    return (
                                      <React.Fragment key={idx}>
                                        {showGroupHeader && (
                                          <div className="px-1 pt-2 pb-1 flex items-center gap-2 text-xs uppercase tracking-wide">
                                            <span className="text-blue-300 font-semibold">{itm.groupDescricao || `Grupo ${grupoAtual}`}</span>
                                            <span className="text-slate-500 normal-case">·</span>
                                            <span className="text-slate-400 normal-case">{arr.filter((x: any) => groupKey(x) === grupoAtual).length} itens no grupo</span>
                                          </div>
                                        )}
                                        <DisputeItemCard
                                          item={itm}
                                          itensGrupoRow={itensGrupoRow}
                                          detalhamento={expandedSession.rankingData?.detalhamentoItem?.[String(itm.itemNumber)]}
                                          lancesItem={expandedSession.rankingData?.lancesPorParticipante?.[String(itm.itemNumber)]?.items}
                                          propostasItem={expandedSession.rankingData?.propostasIniciais?.[String(itm.itemNumber)]?.items}
                                          meuValorUnitario={expandedSession.rankingData?.meuLanceUnitarioPorItem?.[String(itm.itemNumber)]}
                                          valorGlobalLote={ehPcpValorGlobal(expandedSession)}
                                          sessionId={expandedSession.id}
                                          sessionStatus={expandedSession.status}
                                          index={idx}
                                          onLimitChange={loadSessions}
                                          onOpenDrawer={(itemNumber, description) => setDrawerItem({ sessionId: expandedSession.id, itemKey: itemNumber, description })}
                                        />
                                      </React.Fragment>
                                    );
                                  })}
                                  {(!expandedSession.itemLimits || expandedSession.itemLimits.length === 0) && (
                                    <div className="text-center text-slate-500 py-12 text-sm">
                                      Sem itens nesta disputa.
                                    </div>
                                  )}
                                </div>
                                </div>
                              </div>
                              );
                            })()}

                            {/* Resultado Tab — padronizado pra ler de itemLimits + resultData.outcome */}
                            {activeTab === 'resultado' && (() => {
                              const items = expandedSession.itemLimits || [];
                              const outcome = expandedSession.resultData?.outcome || null;

                              // IPCOM por item (a partir de allProposals.ehMinha)
                              type Row = { itemNumber: number; description: string; quantity: number; groupIdentificador?: string; rank: number | null; valor: number | null; total: number | null; winnerCnpj: string | null; winnerName: string | null; winnerValor: number | null; winnerTotal: number | null; };
                              const rows: Row[] = items.map((it: any) => {
                                const props: any[] = Array.isArray(it.allProposals) ? it.allProposals : [];
                                const me = props.find((p: any) => p.ehMinha);
                                // Vencedor real = primeiro NÃO-desclassificado (array já vem ordenado por rank).
                                // Quando rank=1 está desclassificado, o adjudicado é o próximo válido.
                                const winner = props.find((p: any) => !p.desclassificada) || props.find((p: any) => p.rank === 1);
                                const meValor = me ? (me.valorNegociado ?? me.valorOfertado ?? null) : null;
                                const winnerValor = winner ? (winner.valorNegociado ?? winner.valorOfertado ?? null) : null;
                                const qtd = Number(it.quantity) || 1;
                                return {
                                  itemNumber: it.itemNumber,
                                  description: it.description,
                                  quantity: qtd,
                                  groupIdentificador: it.groupIdentificador,
                                  rank: me ? me.rank : null,
                                  valor: meValor,
                                  total: meValor !== null ? meValor * qtd : null,
                                  winnerCnpj: winner ? winner.cnpj : null,
                                  winnerName: winner ? winner.razaoSocial : null,
                                  winnerValor,
                                  winnerTotal: winnerValor !== null ? winnerValor * qtd : null,
                                };
                              });

                              const groups = Array.from(new Set(items.map((i: any) => i.groupIdentificador).filter(Boolean))) as string[];
                              const isByGroup = groups.length > 0;

                              // Ranking AGREGADO por grupo: coleta de itemLimits (groupRanking populado só
                              // no primeiro item de cada grupo). Cada entrada tem rank/cnpj/razão/total
                              // calculados pelo Playwright a partir das chamadas /participacoes/{cnpj}/itens-grupo.
                              type GroupSummary = {
                                identificador: string;
                                descricao?: string;
                                ranking: any[];
                                winner: any | null;
                                ipcom: any | null;
                                itemCount: number;
                              };
                              const groupSummaries: GroupSummary[] = groups.map((id) => {
                                const itemsDoGrupo = items.filter((i: any) => i.groupIdentificador === id);
                                const headerItem = itemsDoGrupo.find((i: any) => Array.isArray(i.groupRanking) && i.groupRanking.length > 0);
                                const ranking: any[] = headerItem?.groupRanking || [];
                                return {
                                  identificador: id,
                                  descricao: headerItem?.groupDescricao,
                                  ranking,
                                  // Vencedor real = primeiro NÃO-desclassificado (não confundir com rank=1).
                                  winner: ranking.find((p: any) => !p.desclassificada) || ranking.find((p: any) => p.rank === 1) || null,
                                  ipcom: ranking.find((p: any) => p.ehMinha) || null,
                                  itemCount: itemsDoGrupo.length,
                                };
                              });
                              const hasGroupRanking = groupSummaries.some((g) => g.ranking.length > 0);

                              const myRows = rows.filter((r) => r.rank !== null);
                              const winnerRows = rows.filter((r) => r.winnerCnpj !== null);
                              const ipcomTotal = myRows.reduce((s, r) => s + (r.total || 0), 0);
                              const winnerTotal = winnerRows.reduce((s, r) => s + (r.winnerTotal || 0), 0);
                              const proposalTotal = items.reduce((s: number, i: any) => s + (Number(i.proposalValue) || 0) * (Number(i.quantity) || 1), 0);
                              const hasMyData = myRows.length > 0;
                              const isWinner = hasMyData && myRows.every((r) => r.rank === 1);
                              const economyPct = (proposalTotal > 0 && winnerTotal > 0) ? Math.round((1 - winnerTotal / proposalTotal) * 100) : null;

                              // Banner de resultado
                              let banner: { label: string; sub: string; classes: string };
                              if (outcome === 'nao_convocado') {
                                banner = {
                                  label: 'Não convocado para a fase fechada',
                                  sub: 'IPCOM não estava no top 3 quando a fase aleatória encerrou — sem direito a envelope final.',
                                  classes: 'bg-rose-500/10 border-rose-500/40 text-rose-200',
                                };
                              } else if (outcome === 'encerrada' && isWinner) {
                                banner = {
                                  label: 'IPCOM venceu',
                                  sub: `1º lugar em ${myRows.length} ${myRows.length === 1 ? 'item' : 'itens'}.`,
                                  classes: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200',
                                };
                              } else if (isWinner) {
                                banner = {
                                  label: 'IPCOM em 1º lugar',
                                  sub: 'Aguarde adjudicação/homologação para confirmar como ganho.',
                                  classes: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-200',
                                };
                              } else if (hasMyData) {
                                const ranks = myRows.map((r) => r.rank!).sort((a, b) => a - b);
                                const labelRanks = ranks.length === 1
                                  ? `${ranks[0]}º lugar`
                                  : `posições ${ranks.slice(0, 3).map((r) => `${r}º`).join(', ')}${ranks.length > 3 ? '…' : ''}`;
                                banner = {
                                  label: `IPCOM em ${labelRanks}`,
                                  sub: 'Disputa encerrada — resultado abaixo.',
                                  classes: 'bg-central-elevado border-central-borda text-slate-200',
                                };
                              } else {
                                banner = {
                                  label: 'Encerrado — sem dados de IPCOM',
                                  sub: 'O ranking ainda não foi coletado para essa sessão. Use "Atualizar ranking" na aba Itens.',
                                  classes: 'bg-slate-500/10 border-slate-500/40 text-slate-300',
                                };
                              }

                              const formatCnpj = (cnpj: string) => cnpj && cnpj.length === 14
                                ? `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
                                : cnpj;

                              return (
                                <div className="p-4 space-y-4">
                                  {/* Banner de resultado */}
                                  <div className={`px-4 py-3 rounded-lg border ${banner.classes}`}>
                                    <div className="font-semibold text-base">{banner.label}</div>
                                    <div className="text-xs mt-0.5 opacity-90">{banner.sub}</div>
                                  </div>

                                  {/* Cards de resumo */}
                                  <div className="grid grid-cols-3 gap-3">
                                    {winnerRows.length > 0 && (() => {
                                      const winners = Array.from(new Set(winnerRows.map((r) => r.winnerCnpj!)));
                                      const primaryWinner = winnerRows[0];
                                      return (
                                        <div className="bg-central-elevado rounded-lg p-3 border border-central-borda">
                                          <p className="text-xs text-slate-500 uppercase">{winners.length === 1 ? 'Vencedor' : `Vencedores (${winners.length})`}</p>
                                          <p className="text-sm font-semibold text-emerald-300 mt-1 truncate" title={primaryWinner.winnerName || ''}>
                                            {winners.length === 1 ? primaryWinner.winnerName : `${primaryWinner.winnerName} +${winners.length - 1}`}
                                          </p>
                                          <p className="text-xs text-slate-400 font-mono">{formatCnpj(primaryWinner.winnerCnpj!)}</p>
                                          <p className="text-lg font-bold text-white mt-1">{fmtCurrency(winnerTotal || primaryWinner.winnerValor || 0)}</p>
                                        </div>
                                      );
                                    })()}
                                    {hasMyData && (
                                      <div className={`rounded-lg p-3 border ${isWinner ? 'bg-emerald-900/20 border-emerald-700' : 'bg-central-elevado border-central-borda'}`}>
                                        <p className="text-xs text-slate-500 uppercase">IPCOM — final</p>
                                        <p className={`text-sm font-semibold mt-1 ${isWinner ? 'text-emerald-300' : 'text-slate-200'}`}>
                                          {myRows.length === 1
                                            ? `${myRows[0].rank}º lugar`
                                            : `${myRows.length} ${myRows.length === 1 ? 'item' : 'itens'}${isByGroup ? ` (em ${groups.length} ${groups.length === 1 ? 'grupo' : 'grupos'})` : ''} — ${myRows.map((r) => `${r.rank}º`).slice(0, 3).join('/')}${myRows.length > 3 ? '…' : ''}`}
                                        </p>
                                        <p className="text-xs text-slate-400">
                                          {myRows[0] && myRows[0].valor !== null ? `lance: ${fmtCurrency(myRows[0].valor)}` : '—'}
                                        </p>
                                        <p className="text-lg font-bold text-white mt-1">{fmtCurrency(ipcomTotal)}</p>
                                      </div>
                                    )}
                                    {proposalTotal > 0 && winnerTotal > 0 && economyPct !== null && (
                                      <div className="bg-central-elevado rounded-lg p-3 border border-central-borda">
                                        <p className="text-xs text-slate-500 uppercase">Economia da disputa</p>
                                        <p className="text-xs text-slate-400 mt-1">Estimado: <span className="text-slate-300">{fmtCurrency(proposalTotal)}</span></p>
                                        <p className="text-xs text-slate-400">Final: <span className="text-slate-300">{fmtCurrency(winnerTotal)}</span></p>
                                        <p className={`text-lg font-bold mt-1 ${economyPct > 0 ? 'text-blue-400' : 'text-slate-400'}`}>
                                          {economyPct > 0 ? `↓ ${economyPct}%` : '—'}
                                        </p>
                                      </div>
                                    )}
                                  </div>

                                  {/* Resultado AGREGADO por grupo (totais por fornecedor — só pra licitações por grupo) */}
                                  {isByGroup && hasGroupRanking && (
                                    <div>
                                      <h4 className="text-sm font-medium text-slate-300 mb-2">
                                        Resultado por grupo (totais agregados)
                                      </h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-xs text-slate-500 uppercase border-b border-central-borda">
                                            <th className="px-3 py-2 text-left w-16">Grupo</th>
                                            <th className="px-3 py-2 text-right w-16">Itens</th>
                                            <th className="px-3 py-2 text-left">Vencedor do grupo</th>
                                            <th className="px-3 py-2 text-right w-32">Total vencedor</th>
                                            <th className="px-3 py-2 text-center w-20">IPCOM</th>
                                            <th className="px-3 py-2 text-right w-32">Total IPCOM</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {groupSummaries.map((g) => {
                                            const ipcomIsWinner = g.ipcom?.rank === 1;
                                            return (
                                              <tr key={g.identificador} className={`border-b border-central-borda ${ipcomIsWinner ? 'bg-emerald-500/5' : ''}`}>
                                                <td className="px-3 py-2 text-blue-300 font-semibold">{g.descricao || g.identificador}</td>
                                                <td className="px-3 py-2 text-right text-slate-400">{g.itemCount}</td>
                                                <td className="px-3 py-2 text-slate-300 max-w-xs truncate" title={g.winner?.razaoSocial || ''}>
                                                  {g.winner ? (
                                                    <>
                                                      <span className="text-[10px] font-mono text-slate-500 mr-1">{g.winner.cnpj && g.winner.cnpj.length === 14 ? `${g.winner.cnpj.slice(0,2)}.${g.winner.cnpj.slice(2,5)}.${g.winner.cnpj.slice(5,8)}/${g.winner.cnpj.slice(8,12)}-${g.winner.cnpj.slice(12)}` : g.winner.cnpj}</span>
                                                      {g.winner.razaoSocial}
                                                    </>
                                                  ) : <span className="text-slate-600">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-emerald-400">
                                                  {g.winner?.valorNegociado != null ? fmtCurrency(g.winner.valorNegociado) : (g.winner?.valorOfertado != null ? fmtCurrency(g.winner.valorOfertado) : '—')}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  {g.ipcom ? (
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                      g.ipcom.rank === 1 ? 'bg-emerald-500/20 text-emerald-400' :
                                                      g.ipcom.rank === 2 ? 'bg-blue-500/20 text-blue-400' :
                                                      g.ipcom.rank === 3 ? 'bg-yellow-500/20 text-yellow-400' :
                                                      'bg-orange-500/20 text-orange-400'
                                                    }`}>{g.ipcom.rank}º</span>
                                                  ) : (
                                                    <span className="text-slate-600 text-xs">—</span>
                                                  )}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono">
                                                  {g.ipcom?.valorNegociado != null ? (
                                                    <span className={ipcomIsWinner ? 'text-emerald-300 font-semibold' : 'text-slate-300'}>
                                                      {fmtCurrency(g.ipcom.valorNegociado)}
                                                    </span>
                                                  ) : g.ipcom?.valorOfertado != null ? (
                                                    <span className="text-slate-300">{fmtCurrency(g.ipcom.valorOfertado)}</span>
                                                  ) : <span className="text-slate-600">—</span>}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {/* Tabela: vencedor x IPCOM por item/grupo */}
                                  {rows.length > 0 && rows.some((r) => r.rank !== null || r.winnerCnpj !== null) && (
                                    <div>
                                      <h4 className="text-sm font-medium text-slate-300 mb-2">
                                        {isByGroup ? 'Resultado por item (agrupado)' : 'Resultado por item'}
                                      </h4>
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-xs text-slate-500 uppercase border-b border-central-borda">
                                            <th className="px-3 py-2 text-left w-12">#</th>
                                            <th className="px-3 py-2 text-left">Descrição</th>
                                            <th className="px-3 py-2 text-right w-20">Qtd</th>
                                            <th className="px-3 py-2 text-left">Vencedor</th>
                                            <th className="px-3 py-2 text-right w-32">Valor unit.</th>
                                            <th className="px-3 py-2 text-center w-20">IPCOM</th>
                                            <th className="px-3 py-2 text-right w-32">IPCOM unit.</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {rows.map((r, i) => {
                                            const ipcomIsWinner = r.rank === 1;
                                            return (
                                              <tr key={i} className={`border-b border-central-borda ${ipcomIsWinner ? 'bg-emerald-500/5' : ''}`}>
                                                <td className="px-3 py-2 text-slate-400">
                                                  {r.groupIdentificador ? <span className="text-blue-300 mr-1">{r.groupIdentificador}/</span> : null}
                                                  {r.itemNumber}
                                                </td>
                                                <td className="px-3 py-2 text-slate-300 max-w-xs truncate" title={r.description}>{r.description}</td>
                                                <td className="px-3 py-2 text-right text-slate-400">{r.quantity}</td>
                                                <td className="px-3 py-2 text-slate-300 max-w-xs truncate" title={r.winnerName || ''}>
                                                  {r.winnerName ? (
                                                    <>
                                                      <span className="text-[10px] font-mono text-slate-500 mr-1">{formatCnpj(r.winnerCnpj!)}</span>
                                                      {r.winnerName}
                                                    </>
                                                  ) : <span className="text-slate-600">—</span>}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-emerald-400">
                                                  {r.winnerValor !== null ? fmtCurrency(r.winnerValor) : '—'}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  {r.rank !== null ? (
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                                      r.rank === 1 ? 'bg-emerald-500/20 text-emerald-400' :
                                                      r.rank === 2 ? 'bg-blue-500/20 text-blue-400' :
                                                      r.rank === 3 ? 'bg-yellow-500/20 text-yellow-400' :
                                                      'bg-orange-500/20 text-orange-400'
                                                    }`}>{r.rank}º</span>
                                                  ) : (
                                                    <span className="text-slate-600 text-xs">não participou</span>
                                                  )}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono">
                                                  {r.valor !== null ? (
                                                    <span className={ipcomIsWinner ? 'text-emerald-300 font-semibold' : 'text-slate-300'}>
                                                      {fmtCurrency(r.valor)}
                                                    </span>
                                                  ) : <span className="text-slate-600">—</span>}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                      <p className="text-[11px] text-slate-500 mt-2">
                                        Para o ranking completo (todos fornecedores), veja a aba <span className="text-slate-300">Itens</span> e expanda os rankings.
                                      </p>
                                    </div>
                                  )}

                                  {rows.every((r) => r.rank === null && r.winnerCnpj === null) && (
                                    <div className="text-center py-6 text-slate-500">
                                      <p>Sem dados de ranking ainda.</p>
                                      <p className="text-xs mt-1">Use "Atualizar ranking" na aba Itens para coletar do portal.</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                            {/* Operations Tab */}
                            {activeTab === 'operations' && (
                              <div className="max-h-60 overflow-y-auto p-3">
                                <div className="space-y-1.5">
                                  {bidLogs.map((log: any) => (
                                    <div key={log.id} className="flex items-center gap-3 text-xs">
                                      <span className="text-slate-500 font-mono whitespace-nowrap">
                                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : ''}
                                      </span>
                                      <span className={`px-1.5 py-0.5 rounded ${
                                        log.result === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                                        log.result === 'queued' || log.result === 'sent' ? 'bg-sky-500/20 text-sky-300' :
                                        log.result === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                        log.result === 'error' ? 'bg-red-500/20 text-red-400' :
                                        log.result === 'dry_run' ? 'bg-blue-500/20 text-blue-300' :
                                        log.result === 'blocked' ? 'bg-amber-500/20 text-amber-300' :
                                        'bg-slate-500/20 text-slate-400'
                                      }`}>
                                        {log.result === 'dry_run' ? '🧪 SIMULADO' : log.result === 'blocked' ? '⛔ BLOQUEADO' : log.result === 'queued' ? '📤 NA FILA' : log.result}
                                      </span>
                                      <span className="text-slate-300">
                                        Item {log.itemNumber} — Lance {fmtCurrency(log.bidValue)}
                                      </span>
                                      <span className="text-slate-500">({log.strategy})</span>
                                      {log.errorMessage && <span className="text-red-400 truncate">{log.errorMessage}</span>}
                                    </div>
                                  ))}
                                  {bidLogs.length === 0 && (
                                    <p className="text-sm text-slate-500 text-center py-4">Nenhuma operação registrada</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* R1.6: Ranking Tab — fornecedores classificados (11 propostas, etc) */}
                            {activeTab === 'ranking' && (
                              <>
                                <DisputeRankingInline session={expandedSession} />
                                {/* D-III — registro do desfecho AQUI só quando a disputa
                                    NÃO foi automática (decisão do Heitor, 26/08).
                                    Quando o robô lança de verdade (`autoBid && dryRun ===
                                    false`, a mesma condição canônica do lance real), o
                                    resultado publicado no PNCP é casado pelo CNPJ e o
                                    desfecho se preenche sozinho — pedir digitação ali seria
                                    convidar a sobrescrever documento com memória.
                                    Disputa no braço não tem essa captura, e é justamente o
                                    caso dos três lotes do PE 021/2026 feitos à mão. */}
                                {(() => {
                                  const cfg = expandedSession?.bidConfig || {};
                                  const roboDisputouDeVerdade =
                                    cfg.autoBid === true && cfg.dryRun === false;
                                  if (roboDisputouDeVerdade) return null;
                                  if (expandedSession.status !== 'finalizado') return null;
                                  if (!expandedSession.itemId) return null;
                                  return (
                                    <div className="mt-4 pt-4 border-t border-central-borda">
                                      <DesfechoPicker
                                        itemId={expandedSession.itemId}
                                        desfecho={expandedSession.item?.desfecho}
                                        desfechoFonte={expandedSession.item?.desfechoFonte}
                                        desfechoObs={expandedSession.item?.desfechoObs}
                                        showToast={showToast}
                                      />
                                      <div className="text-[11px] text-slate-500 mt-1">
                                        Esta disputa não rodou no automático, então o resultado
                                        não é capturado sozinho do PNCP.
                                      </div>
                                    </div>
                                  );
                                })()}
                              </>
                            )}

                            {/* Messages Tab — fonte ÚNICA: `procurement_messages`,
                                 a MESMA tabela do Monitorar, via GET /disputes/:id/messages.
                                 Trocado em 25/08/2026: lia `rankingData.mensagensSessaoPublica`,
                                 blob que só a extensão alimentava e que existia em 2 de 54
                                 sessões (ambas de 19/05) — a aba ficava vazia sem ninguém notar. */}
                            {activeTab === 'messages' && (
                              <DisputeMessagesInline sessionId={expandedSession.id} />
                            )}
                  </div>
                )}
              />
            );
  };

  // ========== Render ==========

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Disputar</h2>
          <p className="text-sm text-slate-400 mt-1">Dispute suas licitações automaticamente</p>
        </div>
        <div className="flex items-center gap-3">
          <ComprasNetHealthDot variant='inline' />
          {/* Backend #533: some sozinho quando não há sessão presa (ou sem permissão). */}
          <EncerrarSessoesPresas onEncerradas={loadSessions} avisar={showToast} />
          <button
            onClick={() => { setShowNewModal(true); loadFormData(); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors"
          >
            <Icon name="plus" className="w-4 h-4" />
            Nova Disputa
          </button>
        </div>
      </div>

      {/* R1.14: Busca global — filtra simultaneamente Cadastrar / Disputar / Monitorar */}
      <div className="relative">
        <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Buscar em todas as abas: órgão, objeto, nº, UASG…"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          className={`w-full rounded-lg pl-9 pr-9 py-2 text-sm placeholder-slate-500 ${campo}`}
        />
        {globalSearch && (
          <button
            onClick={() => setGlobalSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 p-1"
            title="Limpar busca"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        )}
      </div>

      {/* NOTA (24/05/2026): tabs internas Cadastrar/Disputar/Monitorar REMOVIDAS.
          User decidiu menu flat — cada view é acessada apenas pelo menu lateral.
          O conteúdo desta tela agora é só o flow de Disputas. */}

      {/* L7 (O1/L1/R1/V1, 24/10/2026): o resumo diz o que vem a seguir e quanto se ganhou, não a contagem por status. */}
      {(() => {
        const agora = Date.now();
        const limite = agora + JANELA_PROXIMAS_H * 3600_000;
        const prox = sessions.filter((x: any) => secaoDaSessao(x, agora) !== 'finalizadas' && (inicioDaSessao(x) ?? 0) <= limite);
        const sem = propostasSemDisputa(propostas, sessions, agora);
        const semNaJanela = sem.filter((p: any) => new Date(p.item.dataSessaoInicio || p.item.dataEncerramento).getTime() <= limite);
        const emJogo = [...prox.map((x: any) => Number(x.proposal?.totalValue) || 0), ...semNaJanela.map((p: any) => Number(p.totalValue) || 0)].reduce((a, b) => a + b, 0);
        const vitorias = sessions.filter((x: any) => estadoDaSessao(x, agora).vitoria);
        const ganho = vitorias.reduce((a: number, x: any) => a + valorGanho(x), 0);
        return (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-resumo-do-disputar>
            <CartaoDoTopo rotulo={`Próximas ${JANELA_PROXIMAS_H} h`} valor={prox.length + semNaJanela.length} sub={`${fmtCurrency(emJogo)} em jogo`} />
            <CartaoDoTopo rotulo="Sem disputa configurada" valor={propostasFalharam ? '—' : sem.length}
              sub={propostasFalharam ? 'não consegui ler as propostas' : 'sessão marcada e nenhuma disputa criada'} barra={!propostasFalharam && sem.length > 0 ? 'warning' : null} />
            <CartaoDoTopo rotulo="Ganho" valor={fmtCurrency(ganho)} sub={`${vitorias.length} ${vitorias.length === 1 ? 'vencedora confirmada' : 'vencedoras confirmadas'}`} />
          </div>
        );
      })()}

      {/* Empty State */}
      {sessions.length === 0 && (
        <div className="text-center py-16 bg-central-cartao rounded-central border border-central-borda">
          <Icon name="target" className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">Nenhuma disputa configurada</h3>
          <p className="text-sm text-slate-500 mb-4">Crie uma nova disputa para começar a disputar licitações automaticamente</p>
          <button
            onClick={() => { setShowNewModal(true); loadFormData(); }}
            className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm"
          >
            <Icon name="plus" className="w-4 h-4 inline mr-1" /> Nova Disputa
          </button>
        </div>
      )}

      {/* L7 (O1, Heitor 24/10/2026): Hoje / ao vivo, Próximas e Finalizadas. As presas ("configurando" com a sessão já
          passada) caem em Finalizadas, onde o "Encerrar sessões presas" as acha; nunca em Hoje nem em Próximas. */}
      {(() => {
        const agora = Date.now();
        const comEstado = filteredSessions.map((x: any) => ({ s: x, ...estadoDaSessao(x, agora) }));
        const ini = (x: any) => inicioDaSessao(x) ?? 0;
        const hoje = comEstado.filter((x) => x.secao === 'hoje').sort((a, b) => ini(a.s) - ini(b.s));
        const q = globalSearch.trim().toLowerCase();
        const sem = propostasSemDisputa(propostas, sessions, agora).filter((p: any) => !q
          || `${p.item?.orgaoNome || ''} ${p.item?.numeroCompra || ''} ${p.item?.objetoCompra || ''}`.toLowerCase().includes(q));
        const proximas: Array<{ t: number; s?: any; p?: any; estado?: EstadoDoCartao; secao?: SecaoDaDisputa }> = [
          ...comEstado.filter((x) => x.secao === 'proximas').map((x) => ({ t: ini(x.s), s: x.s, estado: x.estado, secao: x.secao })),
          ...sem.map((p: any) => ({ t: new Date(p.item.dataSessaoInicio || p.item.dataEncerramento).getTime(), p })),
        ].sort((a, b) => a.t - b.t);
        const finalizadasTodas = comEstado.filter((x) => x.secao === 'finalizadas').sort((a, b) => ini(b.s) - ini(a.s));
        const vitorias = finalizadasTodas.filter((x) => x.vitoria);
        const ganho = vitorias.reduce((acc, x) => acc + valorGanho(x.s), 0);
        const corte = agora - DIAS_DAS_FINALIZADAS_RECENTES * 86400_000;
        const bf = buscaFinalizadas.trim().toLowerCase();
        const finalizadas = finalizadasTodas
          .filter((x) => !bf || `${x.s.item?.orgaoNome || ''} ${x.s.item?.unidadeNome || ''} ${x.s.item?.numeroCompra || ''}`.toLowerCase().includes(bf))
          .filter((x) => mostrarAntigas || bf || ini(x.s) >= corte);
        const antigasEscondidas = !mostrarAntigas && !bf ? finalizadasTodas.filter((x) => ini(x.s) < corte).length : 0;
        const alternarFinalizadas = () => { const n = !finalizadasAbertas; setFinalizadasAbertas(n); gravarFinalizadasAbertas(n); };
        const tituloDaSecao = (t: string, sub?: string) => (
          <div className="flex items-baseline justify-between mt-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t}</h3>
            {sub && <span className="text-xs text-slate-500">{sub}</span>}
          </div>
        );
        return (
          <div className="space-y-3" data-lista-do-disputar>
            <section data-secao="hoje" className="space-y-3">
              {tituloDaSecao('Hoje / ao vivo', hoje.length ? undefined : 'nenhuma sessão hoje')}
              {hoje.map((x) => renderSessao(x.s, x.estado, x.secao))}
            </section>
            <section data-secao="proximas" className="space-y-3">
              {tituloDaSecao('Próximas', proximas.length ? `as das próximas ${JANELA_PROXIMAS_H} h entram no resumo` : 'nenhuma sessão marcada')}
              {proximas.map((x) => x.s ? renderSessao(x.s, x.estado, x.secao) : (
                <div key={`sem-${x.p.id}`} data-sem-disputa={x.p.id}
                  className="bg-central-cartao border border-central-borda border-l-[3px] border-l-amber-500 rounded-central px-4 py-3 flex flex-wrap items-center gap-4">
                  <div className="w-40 shrink-0">
                    <div className="text-sm font-semibold text-white whitespace-nowrap">{fmtDateTime(x.p.item.dataSessaoInicio || x.p.item.dataEncerramento)}</div>
                    <div className="text-[11px] text-slate-400">{contagem(x.t, agora)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-100 truncate">{getOrgaoDisplay(x.p.item)}{x.p.item.numeroCompra ? ` · nº ${x.p.item.numeroCompra}` : ''}</div>
                    <div className="text-xs text-slate-400 truncate" title={x.p.item.objetoCompra}>{x.p.item.objetoCompra}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">proposta enviada {fmtCurrency(Number(x.p.totalValue) || 0)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${TONE_BADGE.warning}`}>Sem disputa configurada</span>
                    <button type="button" onClick={() => { setPreNovaDisputa(x.p.item.id); setShowNewModal(true); loadFormData(); }}
                      className="px-3 py-1.5 text-xs rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto">
                      Criar disputa
                    </button>
                  </div>
                </div>
              ))}
            </section>
            <section data-secao="finalizadas" className="space-y-3">
              <button type="button" onClick={alternarFinalizadas} aria-expanded={finalizadasAbertas} data-cabecalho-finalizadas
                className="w-full flex items-center justify-between px-4 py-3 rounded-central border border-central-borda bg-central-cartao hover:bg-central-elevado text-left">
                <span className="text-sm text-slate-200">{resumoDasFinalizadas(finalizadasTodas.length, vitorias.length, ganho, fmtCurrency)}</span>
                <span className="text-xs text-slate-400">{finalizadasAbertas ? 'recolher' : 'mostrar'}</span>
              </button>
              {finalizadasAbertas && (
                <>
                  <input type="text" value={buscaFinalizadas} onChange={(e) => setBuscaFinalizadas(e.target.value)} aria-label="Buscar nas finalizadas"
                    placeholder="Buscar nas finalizadas por órgão ou número…" className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 ${campo}`} />
                  {finalizadas.map((x) => renderSessao(x.s, x.estado, x.secao))}
                  {finalizadas.length === 0 && <div className="text-sm text-slate-500 px-1">Nenhuma finalizada {bf ? 'com essa busca' : `nos últimos ${DIAS_DAS_FINALIZADAS_RECENTES} dias`}.</div>}
                  {antigasEscondidas > 0 && (
                    <button type="button" onClick={() => setMostrarAntigas(true)} className="text-xs text-slate-400 underline hover:text-white">
                      ver mais antigas ({antigasEscondidas})
                    </button>
                  )}
                </>
              )}
            </section>
          </div>
        );
      })()}

      {sessions.length > 0 && filteredSessions.length === 0 && (
        <div className="text-center py-16 text-slate-400">Nenhuma disputa encontrada com essa busca</div>
      )}

      {/* ========== New Dispute Modal ========== */}
      {showNewModal && (() => {
        const selectItem = (item: any) => {
          let autoPortalId = '';
          if (item?.portalOrigem) {
            const source = item.portalOrigem.toLowerCase();
            const match = portals.find((pt: any) => pt.portalName?.toLowerCase().includes(source));
            if (match) autoPortalId = match.id.toString();
          }
          const fmtDT = (d: any) => {
            if (!d) return '';
            const dt = new Date(d);
            const pad = (n: number) => String(n).padStart(2, '0');
            return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
          };
          // Disputa começa SEMPRE quando proposta encerra (dataEncerramento)
          // — vale para qualquer modalidade. Preferência: dataSessaoInicio/Fim
          // quando o PNCP retorna esses campos. Quando ausentes, fallback por
          // modalidade:
          //   Dispensa (8): janela fixa +6h (típico do portal Compras.gov.br)
          //   Pregão (6) / Concorrência (4): sem fim fixo → +5h como margem
          //                                  (bot detecta fim real via DOM)
          // Bug 27/04 Dispensa 11/2026 CRM-TO: form pré-preenchia 10/04 16:08
          // (dataAbertura, início do cadastro de proposta) em vez de 27/04
          // 08:30 (dataEncerramento, abertura da disputa).
          // F-4 (Onda 2): modalidadeNome decide (id cru colide: PNCP 6=PREGÃO).
          const isDispensa = resolveModalidade(item) === 'dispensa';
          const sessaoInicio = item?.dataSessaoInicio ? fmtDT(item.dataSessaoInicio) : '';
          const sessaoFim = item?.dataSessaoFim ? fmtDT(item.dataSessaoFim) : '';
          let fallbackStart = '';
          let fallbackEnd = '';
          if (item?.dataEncerramento) {
            const dtStart = new Date(item.dataEncerramento);
            // PNCP convenção: dataEncerramentoProposta termina em :59, disputa
            // abre :00 da próxima hora. Normaliza só no fallback (quando
            // dataSessaoInicio ainda não veio do scrape).
            if (isDispensa && dtStart.getMinutes() === 59) {
              dtStart.setMinutes(dtStart.getMinutes() + 1);
            }
            fallbackStart = fmtDT(dtStart);
            // Pregão NÃO tem fim fixo (prorrogações automáticas, pregoeiro abre
            // item a item). Não pré-preenche endTime — bot detecta fim real via
            // dataHoraFimContagem do portal. Bug 25/05: pre-fill +5h marcava
            // session como Finalizada prematuramente.
            if (isDispensa) {
              const dtEnd = new Date(dtStart);
              dtEnd.setHours(dtEnd.getHours() + 6);
              fallbackEnd = fmtDT(dtEnd);
            }
          }
          const defaultStart = sessaoInicio || fallbackStart;
          const defaultEnd = sessaoFim || fallbackEnd;
          setDatesDerived(!sessaoInicio || !sessaoFim);
          setNewForm({
            itemId: String(item.id),
            portalId: autoPortalId || newForm.portalId,
            startTime: defaultStart || newForm.startTime,
            endTime: defaultEnd || newForm.endTime,
          });
          setItemSearch(`${getOrgaoDisplay(item) || ''} — ${item.numeroCompra || item.pncpId}`);
          setItemDropdownOpen(false);
        };
        const pendente = preNovaDisputa ? items.find((it: any) => it.id === preNovaDisputa) : null;
        const selectedItem = items.find((it: any) => it.id === Number(newForm.itemId));
        const filteredItems = itemSearch.length >= 2
          ? items.filter((it: any) => {
              const s = itemSearch.toLowerCase();
              return (it.orgaoNome || '').toLowerCase().includes(s) ||
                (it.unidadeNome || '').toLowerCase().includes(s) ||
                (it.numeroCompra || '').toLowerCase().includes(s) ||
                (it.pncpId || '').toLowerCase().includes(s) ||
                (it.codigoUnidade || '').includes(s) ||
                (it.objetoCompra || '').toLowerCase().includes(s);
            })
          : items.slice(0, 20);

        return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowNewModal(false)}>
          <div className="bg-central-cartao rounded-central border border-central-borda w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            {/* L7 (L1): o "Criar disputa" das Próximas chega com o item já escolhido; o operador confere e cria. */}
            {pendente && <AoMontar key={pendente.id} onMontar={() => { selectItem(pendente); setPreNovaDisputa(null); }} />}
            <div className="flex items-center justify-between px-5 py-4 border-b border-central-borda">
              <h3 className="text-lg font-semibold text-white">Nova Disputa</h3>
              <button onClick={() => setShowNewModal(false)} className="text-slate-400 hover:text-white">
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Mode tabs */}
              <div className="flex gap-1 bg-central-elevado p-1 rounded-lg">
                <button onClick={() => setNewMode('select')} className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${newMode === 'select' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>Selecionar</button>
                <button onClick={() => setNewMode('manual')} className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${newMode === 'manual' ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>Cadastro Manual</button>
              </div>

              {newMode === 'select' ? (
                <div className="relative">
                  <label className="block text-sm text-slate-400 mb-1">Licitação *</label>
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => { setItemSearch(e.target.value); setItemDropdownOpen(true); if (!e.target.value) setNewForm({ ...newForm, itemId: '' }); }}
                    onFocus={() => setItemDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setItemDropdownOpen(false), 200)}
                    placeholder="Pesquisar por órgão, UASG, número..."
                    className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 ${campo}`}
                  />
                  {selectedItem && <p className="text-xs text-emerald-400 mt-1">{getOrgaoDisplay(selectedItem)} — {selectedItem.numeroCompra} ({selectedItem.modalidade || 'DE'})</p>}
                  {itemDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-central-borda rounded-lg shadow-xl max-h-48 overflow-y-auto">
                      {filteredItems.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">Nenhuma licitação encontrada</div>
                      ) : filteredItems.map((it: any) => (
                        <button
                          key={it.id}
                          onClick={() => selectItem(it)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-600 transition-colors ${newForm.itemId === String(it.id) ? 'bg-slate-600 text-white' : 'text-slate-300'}`}
                        >
                          <span className="font-medium">{getOrgaoDisplay(it) || 'Sem órgão'}</span>
                          <span className="text-slate-400"> — {it.numeroCompra || it.pncpId} ({it.modalidade || 'DE'})</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">UASG *</label>
                      <input type="text" value={manualForm.codigoUnidade} onChange={(e) => setManualForm({ ...manualForm, codigoUnidade: e.target.value })} placeholder="Ex: 240106" className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 ${campo}`} />
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Nº Compra *</label>
                      <input type="text" value={manualForm.numeroCompra} onChange={(e) => setManualForm({ ...manualForm, numeroCompra: e.target.value })} placeholder="Ex: 350" className={`w-full rounded-lg px-3 py-2 text-sm placeholder-slate-500 ${campo}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Portal</label>
                      <select value={manualForm.portal} onChange={(e) => setManualForm({ ...manualForm, portal: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                        <option value="comprasnet">Compras.gov.br</option>
                        <option value="bnc">BNC</option>
                        <option value="bll">BLL</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Modalidade</label>
                      <select value={manualForm.modalidade} onChange={(e) => setManualForm({ ...manualForm, modalidade: e.target.value })} className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}>
                        <option value="de">Dispensa Eletrônica</option>
                        <option value="pe">Pregão Eletrônico</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-400 mb-1">Portal de Acesso *</label>
                <select
                  value={newForm.portalId}
                  onChange={(e) => setNewForm({ ...newForm, portalId: e.target.value })}
                  className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                >
                  <option value="">Selecione um portal...</option>
                  {portals.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.portalName}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Início Disputa</label>
                  <input
                    type="datetime-local"
                    value={newForm.startTime}
                    onChange={(e) => setNewForm({ ...newForm, startTime: e.target.value })}
                    className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Fim Disputa *</label>
                  <input
                    type="datetime-local"
                    value={newForm.endTime}
                    onChange={(e) => setNewForm({ ...newForm, endTime: e.target.value })}
                    className="w-full bg-slate-700 border border-central-borda rounded-lg px-3 py-2 text-sm text-white"
                  />
                </div>
              </div>
              {datesDerived && newForm.startTime && (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-700/40 rounded px-2 py-1.5">
                  ⚠ Horário <strong>estimado</strong> (sessão pública ainda não publicada pelo portal). Confira na página de disputa antes de salvar — o scrape de status atualiza 3×/dia.
                </div>
              )}
              <p className="text-xs text-slate-500">Preencha com o horário da página de disputa do portal (ex: Dispensa 6h ou 8h). O robô também detecta automaticamente ao iniciar.</p>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-central-borda">
              <button onClick={() => setShowNewModal(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">
                Cancelar
              </button>
              <button onClick={handleCreate} disabled={manualLoading} className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm disabled:opacity-50">
                {manualLoading ? 'Criando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ========== Config Modal ========== */}
      {showConfigModal && configSession && (
        <ConfigModal
          session={configSession}
          onClose={() => { setShowConfigModal(false); setConfigSessionId(null); }}
          onSave={handleSaveConfig}
          onUpdate={(updated: any) => {
            setSessions((prev) => prev.map((s) => s.id === updated.id ? { ...s, ...updated } : s));
          }}
        />
      )}
      {/* R1.5: drawer lateral de lances/propostas/concorrentes */}
      {drawerItem && (
        <DisputeRankingDrawer
          sessionId={drawerItem.sessionId}
          itemKey={drawerItem.itemKey}
          itemDescription={drawerItem.description}
          onClose={() => setDrawerItem(null)}
        />
      )}

      {/* Modal de Lance Final Fechado — abre automaticamente quando IPCOM é
          convocada (fase=LF/AF + convocado=true). Fechar sem submit = manter
          último lance (5.11.3). */}
      {lfModalSession && (
        <LanceFechadoModal
          // F-9 (Onda 2): sessão VIVA do estado (o poll de 3s atualiza sessions;
          // antes o modal congelava o snapshot do instante da convocação —
          // prorrogação/melhor novo do portal não apareciam durante o LF).
          session={sessions.find((s) => s.id === lfModalSession.id) || lfModalSession}
          showToast={showToast}
          onClose={() => {
            lfDismissedRef.current.add(lfModalSession.id);
            setLfModalSession(null);
          }}
          onSent={() => {
            loadSessions();
          }}
        />
      )}
    </div>
  );
};

// ========== Unidade de disputa (grupo × item solto) ==========
// Uma compra pode misturar GRUPO (julgado pelo TOTAL dos itens) com ITEM SOLTO
// (julgado sozinho) — e cada unidade abre, encerra e é vencida separadamente.
// Tratar a lista como plana engana em duas frentes: mostra "cobre/acima" por item
// num grupo onde só o total decide, e soma pisos de unidades diferentes num número
// só. Foi o que aconteceu no PE 15/2026 CRN-8 (grupo de 3 + item 4 solto), onde o
// modal exibia "Soma dos pisos" com aviso de piso alto comparando maçã com laranja.
type UnidadeDisputa = {
  chave: string;
  ehGrupo: boolean;
  rotulo: string;
  membros: Array<{ it: any; idx: number }>;
};

// ⚠️ A CHAVE vem do util canônico (`utils/unidadeDisputa`), nunca de uma regra
// local. Esta função tinha a própria cópia com a ordem INVERTIDA
// (`groupIdentificador ?? groupNumber`), e o util documenta por que a ordem
// importa: no BLL 021/2026 o `groupIdentificador` vem colapsado ('1','1','1')
// enquanto o `groupNumber` é 1,2,3 — pela ordem invertida os três lotes viravam
// UMA unidade aqui no ConfigModal, com os pisos somados, que é exatamente o
// erro que o helper foi criado para matar (e que o guardrail check:unidade
// cobre no backend, mas não alcançava o front).
function agruparPorUnidade(limits: any[]): UnidadeDisputa[] {
  const mapa = new Map<string, UnidadeDisputa>();
  (limits || []).forEach((it: any, idx: number) => {
    const grupo = it?.groupNumber ?? it?.groupIdentificador;
    const ehGrupo = grupo !== null && grupo !== undefined && grupo !== '';
    const chave = chaveUnidade(it);
    if (!mapa.has(chave)) {
      mapa.set(chave, {
        chave,
        ehGrupo,
        // ⚠️ em itemLimits o campo é `groupDescricao` (o `groupDescription` só
        // existe em proposalItems) — aceita os dois pra não depender da origem.
        rotulo: ehGrupo
          ? (it.groupDescricao || it.groupDescription || `Grupo ${grupo}`)
          : `Item ${it?.itemNumber ?? idx + 1}`,
        membros: [],
      });
    }
    mapa.get(chave)!.membros.push({ it, idx });
  });
  // Array.from em vez de spread do iterator: o tsconfig deste projeto tem target
  // abaixo de es2015 e o spread de Map.values() não compila.
  return Array.from(mapa.values());
}

/** Σ (valor × qtd) dos membros — é assim que o portal julga um grupo.
 *  PCP "Por Valor Global" (`valorGlobal`): o bestBid de cada item JÁ É o total
 *  do lote (espelhado pelo backend em todos os itens do groupNumber) — lê o de
 *  um item, sem × qtd. Antes o modal mostrava "melhor R$ 44.298.600" (global do
 *  lote × quantidades) na sessão 126 (08/09/2026). */
function somaUnidade(membros: Array<{ it: any }>, campo: 'limitValue' | 'bestBid', fonte?: any[], valorGlobal = false): number {
  if (campo === 'bestBid' && valorGlobal) {
    const doLote = membros.map(({ it }) => (fonte || []).find((l: any) => l.itemNumber === it.itemNumber)).filter(Boolean);
    return valorGlobalDaUnidade(doLote, 'bestBid') ?? 0;
  }
  return membros.reduce((s, { it }) => {
    const base = campo === 'limitValue'
      ? Number(it.limitValue)
      : Number((fonte || []).find((l: any) => l.itemNumber === it.itemNumber)?.bestBid);
    return s + (Number.isFinite(base) ? base : 0) * (Number(it.quantity) || 1);
  }, 0);
}

// ========== Config Modal Component ==========

const ConfigModal: React.FC<{
  session: any;
  onClose: () => void;
  onSave: (strategies: any, bidConfig: any, itemLimits?: any[], situacao?: { vaiLancarAgora?: boolean }) => Promise<boolean> | boolean | void;
  onUpdate: (s: any) => void;
}> = ({ session, onClose, onSave, onUpdate }) => {
  // Confirmação padrão do HUB (§8) — o ConfigModal é onde mora o botão de
  // LANCE REAL, então precisa do hook por conta própria.
  const confirm = useConfirm();
  const [strategies, setStrategies] = useState(session.strategies || { coverOwnBid: false, bestPosition: true, bidLimitValue: true });
  // Default decimalPlaces=4 pra ComprasNet (HAR 27/05/26 confirmou que o portal
  // usa 4 casas no valor unitário de lance). Outros portais: 2 (default histórico).
  // Botão "Buscar do portal" abaixo continua refinando com o valor real lido.
  const isComprasNet = /comprasnet/i.test(session?.portal?.portalName || '');
  // BLL e BNC = mesma plataforma (base gkz): mesmo comportamento de preview/disputa.
  const isBNC = /bnc/i.test(session?.portal?.portalName || '');
  const isBLL = /bll|bnc/i.test(session?.portal?.portalName || '');
  // Modalidade (pra UX do modal de config): 5=Pregão, 6/8=Dispensa. Usado pra
  // mostrar "Início dos lances" só em Pregão e "Lance nos últimos segundos" só em
  // Dispensa (sniping não funciona em Pregão — prorroga 2min a cada lance no fim).
  // Fallback p/ item.modalidadeId (6/8=Dispensa, mesma convenção do card/relógio
  // em ~:1585): participacao.modalidade só é preenchido APÓS coletar o ranking.
  // Antes disso (sessão "configurando") o modal não sabia que era Dispensa e
  // mostrava "Início dos lances" indevidamente.
  // F-4 (Onda 2): participacao.modalidade (portal, confiável) > modalidadeNome > id cru.
  const cfgIsDispensa = resolveModalidade(session?.item, session?.rankingData?.participacao?.modalidade) === 'dispensa';
  // PCP "Por Valor Global": lote disputado por um valor só (ver utils/unidadeDisputa).
  const isPcpGlobal = ehPcpValorGlobal(session);
  // ⛔ O DEFAULT AQUI PRECISA ESPELHAR O DO BACKEND (`dispute.service.ts` ~436).
  // Dispensa 4/2026 IPC Cascavel (sessão 131, 10/09/2026, REAL): o backend cria
  // a sessão com `dispensaLastSeconds: isDispensa` (true/120 em dispensa) e este
  // default dizia `false/60` SEMPRE — dois defaults para o mesmo campo, e quem
  // gravasse por último vencia. A sessão nasceu com a janela ligada, um Salvar
  // posterior a desligou sem ninguém pedir, e ao virar Lance REAL o robô
  // disparou 2 lances irreversíveis em 6 s, 22 minutos antes da hora planejada.
  const [bidConfig, setBidConfig] = useState(session.bidConfig || {
    startMode: cfgIsDispensa ? 'timer' : 'aberto', timerStartMinutes: 5,
    decimalPlaces: isComprasNet ? 4 : 2,
    reductionType: 'percentage', reductionMin: 1.0, reductionMax: 1.01,
    dispensaLastSeconds: cfgIsDispensa, dispensaLastSecondsValue: cfgIsDispensa ? 120 : 60,
  });
  // ⛔ `useState(x)` só usa `x` na PRIMEIRA renderização. Se o modal montou antes
  // de `session.bidConfig` chegar, o default acima cola no estado e o próximo
  // Salvar grava ele POR CIMA da config real. Re-hidrata quando o servidor muda.
  const bidConfigServidor = JSON.stringify(session.bidConfig ?? null);
  useEffect(() => {
    if (session.bidConfig) setBidConfig((prev: any) => ({ ...prev, ...session.bidConfig }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidConfigServidor]);
  // ComprasNet: força 4 casas no load se a sessão herdou um valor antigo
  // (default histórico era 2). Mantém input editável caso user precise mudar.
  useEffect(() => {
    if (isComprasNet && Number(bidConfig.decimalPlaces) !== 4 && session.bidConfig) {
      setBidConfig((prev: any) => ({ ...prev, decimalPlaces: 4 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Piso (valor limite) editável por item. Mudar com a disputa em curso pede
  // confirmação no handleSaveConfig (pai).
  const [editedLimits, setEditedLimits] = useState<any[]>(() => (session.itemLimits || []).map((it: any) => ({ ...it })));
  // Texto CRU do campo de piso enquanto está sendo digitado, por índice. O
  // número parseado vive em `editedLimits`; este aqui existe só para a pessoa
  // conseguir digitar "40240," sem o campo se reescrever embaixo do dedo. Sai
  // no blur, e aí o campo volta a mostrar o valor que de fato vale.
  const [rascunhoPiso, setRascunhoPiso] = useState<Record<number, string>>({});

  // Preview portal rules: scrape sob demanda das regras do portal antes de
  // iniciar a disputa. Permite o user conferir + aplicar valores corretos
  // (tipoVariacao, variacaoMinima, modoDisputa, tempoIntencoesRecurso, casas)
  // em vez de esperar a primeira coleta de ranking corrigir.
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [scrapingItems, setScrapingItems] = useState(false);
  const [scrapeItemsError, setScrapeItemsError] = useState<string | null>(null);
  const [previewRules, setPreviewRules] = useState<{
    tipoVariacao: 'P' | 'V' | null;
    variacaoMinima: number | null;
    modoDisputa: string | null;
    tempoIntencoesRecurso: number | null;
    casasDecimais: number | null;
    reductionType?: 'percentage' | 'fixed';
  } | null>(null);
  // Constrói o patch do bidConfig a partir das regras lidas do portal.
  // Aplicado automaticamente em handlePreviewPortal (Buscar do portal) — o
  // botão "Aplicar X alterações" virou backup pra reaplicar caso o user
  // mexa nos campos depois.
  const buildPreviewPatch = (rules: NonNullable<typeof previewRules>): any => {
    const patch: any = {};
    if (rules.reductionType) patch.reductionType = rules.reductionType;
    if (rules.variacaoMinima != null) {
      patch.reductionMin = rules.variacaoMinima;
      // ⛔ Aqui a prévia também escrevia `reductionMax = min + 1.5` para "garantir
      // espaço estratégico". A "Variação máxima" foi removida da UI em 20/08/2026
      // porque não participa de decisão nenhuma — todo lance sai pelo MÍNIMO —
      // mas o preview continuou GRAVANDO o campo. Sessão nova nascia com um valor
      // sintético que ninguém lê e que, se alguém religar a leitura no futuro,
      // pareceria curado. Parou de escrever; o campo segue no jsonb por compat
      // com as sessões antigas.
    }
    if (rules.casasDecimais != null) patch.decimalPlaces = rules.casasDecimais;
    return patch;
  };
  const handlePreviewPortal = async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      // BLL: a prévia lê as regras direto da ProcessView (HTML público) no
      // backend — sem login, sem token de disputa, sem prompt. Se falhar, o
      // erro já orienta (ex.: rodar "Sincronizar BLL" pra popular o linkOrigem).
      const res = await disputeService.previewPortalRules(session.id);
      if (!res?.success) {
        setPreviewError(res?.message || 'Regras não disponíveis');
        setPreviewRules(null);
      } else {
        setPreviewRules(res.rules);
        // Aplica direto — não espera o user clicar "Aplicar X alterações".
        const patch = buildPreviewPatch(res.rules);
        if (Object.keys(patch).length > 0) {
          setBidConfig({ ...bidConfig, ...patch });
        }
      }
    } catch (err: any) {
      setPreviewError(err?.response?.data?.message || err?.message || 'Falha ao buscar regras');
      setPreviewRules(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const handleScrapeItems = async () => {
    setScrapingItems(true);
    setScrapeItemsError(null);
    try {
      // Puxa itens+valores do portal (disputa criada manual sem itens). O backend
      // sincroniza a proposta se preciso e devolve a sessão com itemLimits.
      const updated = await disputeService.scrapeItems(session.id);
      const limits = (updated?.itemLimits || []).map((it: any) => ({ ...it }));
      if (limits.length === 0) {
        setScrapeItemsError('Portal não retornou itens. Confirme a proposta no portal.');
      } else {
        setEditedLimits(limits);
        onUpdate({ ...session, itemLimits: limits });
      }
    } catch (err: any) {
      setScrapeItemsError(err?.response?.data?.message || err?.message || 'Falha ao buscar itens do portal');
    } finally {
      setScrapingItems(false);
    }
  };
  const applyPreviewRules = () => {
    if (!previewRules) return;
    const patch = buildPreviewPatch(previewRules);
    setBidConfig({ ...bidConfig, ...patch });
  };

  // Evita fechar o modal quando o usuário seleciona texto num input e solta o
  // mouse fora (mousedown no input → mouseup no backdrop dispara click no
  // backdrop). Só fecha se o mousedown TAMBÉM começou no backdrop.
  const downOnBackdrop = useRef(false);

  // R1.11: Regra do portal pra variação mínima entre lances — usado nas validações inline dos inputs
  const portalVariacao = useMemo(() => {
    const detItens = session?.rankingData?.detalhamentoItem || {};
    const ig = session?.rankingData?.itensGrupo?.['-1'];
    const itens: any[] = Array.isArray(ig) ? ig : (ig?.registros || ig?.itens || ig?.items || []);
    const vals = itens
      .map((i: any) => detItens[String(i.numero)])
      .filter(Boolean)
      .map((d: any) => ({ val: Number(d.variacaoMinimaEntreLances), tipo: d.tipoVariacaoMinimaEntreLances }))
      .filter((v: any) => isFinite(v.val));
    if (vals.length === 0) return { min: null as number | null, tipo: null as string | null };
    return { min: Math.max(...vals.map((v: any) => v.val)), tipo: vals[0].tipo };
  }, [session?.rankingData]);

  // Unidades de disputa da sessão (cada lote/grupo por si). Mesma chave do
  // backend: groupNumber e groupIdentificador juntos — o collectResult já
  // sobrescreveu o groupIdentificador de todos com o do primeiro numa sessão
  // real, e olhar só ele colapsava 3 lotes em 1.
  const unidadesDaSessao = useMemo(() => {
    const lim: any[] = Array.isArray(session?.itemLimits) ? session.itemLimits : [];
    const chaves = new Set<string>();
    for (const il of lim) {
      const g = il?.groupNumber ?? '';
      const gi = il?.groupIdentificador ?? '';
      chaves.add(g === '' && gi === '' ? `item-${il?.itemNumber}` : `${g}|${gi}`);
    }
    return chaves.size;
  }, [session?.itemLimits]);

  const [julgamentoAgrupamento, setJulgamentoAgrupamento] = useState<'por-lote' | 'global'>(
    session?.item?.julgamentoAgrupamento === 'global' ? 'global' : 'por-lote',
  );

  const handleSave = async () => {
    // R1.11: força sniping=false em Pregão (não funciona com prorrogação automática)
    const part = session?.rankingData?.participacao;
    const isPregao = Number(part?.modalidade) === 5;
    const finalBidConfig = isPregao && bidConfig.dispensaLastSeconds
      ? { ...bidConfig, dispensaLastSeconds: false }
      : bidConfig;
    // ⛔ SALVAR PODE LANÇAR AGORA — e lance real não volta atrás.
    // Sessão 131 (10/09/2026): o operador trocou Simulação→Real com a janela de
    // sniping desligada e 2 lances reais saíram 6 segundos depois, queimando a
    // surpresa 22 minutos antes do fim. O aviso do modal dizia só que a mudança
    // "vale na hora" — não que ela DISPARA na hora. (O outro aviso desta tela,
    // logo acima, cobre o caso INVERSO: simulação ligada DENTRO da janela.)
    const disputaViva = ['ativo', 'aguardando'].includes(String(session.status));
    const vaiLancarAgora = disputaViva
      && finalBidConfig.autoBid === true
      && finalBidConfig.dryRun === false
      && !finalBidConfig.dispensaLastSeconds;
    // O aviso continua (é o da sessão 131), mas é a pergunta ÚNICA do salvar, no pai (handleSaveConfig): o
    // window.confirm nativo que vinha antes dela saiu em 14/10/2026.
    // bestPosition/bidLimitValue agora são implícitos (escalação + piso) — força
    // true pra compat com a extension R3, que ainda os checa. O controle do user
    // é só Posição-alvo (top-N) + Cobrir próprio lance. (refactor 29/05/2026)
    const finalStrategies = { ...strategies, bestPosition: true, bidLimitValue: true };
    // Só atualiza a sessão na tela se a pessoa confirmou e o servidor gravou (antes atualizava e depois perguntava).
    const salvou = await onSave(finalStrategies, finalBidConfig, editedLimits, { vaiLancarAgora });
    if (salvou !== false) onUpdate({ ...session, strategies: finalStrategies, bidConfig: finalBidConfig, itemLimits: editedLimits });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (e.target === e.currentTarget && downOnBackdrop.current) onClose(); }}
    >
      <div className="bg-central-cartao rounded-central border border-central-borda w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-central-borda sticky top-0 bg-central-cartao z-10">
          <h3 className="text-lg font-semibold text-white">Configurações da Disputa</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Exigências do portal (PRÉVIA) — antes de iniciar a disputa o bot
              ainda não capturou /participacao. Mostramos o que dá pra derivar
              dos próprios valores (casas decimais) + aviso de que o resto
              (variação mínima, modo de disputa) é lido do portal ao iniciar. */}
          {(() => {
            if (session?.rankingData?.participacao) return null; // bloco completo abaixo
            // Default por portal (HAR confirmou):
            //   ComprasNet → 4 casas decimais (valor unitário, ex.: "R$ 13.000,0000")
            //   BLL → 2 casas decimais (padrão histórico do portal)
            //   Outros (BNC/PCP/etc) → 2 (fallback seguro)
            // Real é lido do portal ao Iniciar; aqui mostramos a sugestão padrão.
            const dec = isComprasNet ? 4 : 2;
            const portalLabel = isComprasNet ? 'ComprasNet' : isBNC ? 'BNC' : isBLL ? 'BLL' : (session?.portal?.portalName || 'portal');
            const mismatch = dec !== bidConfig.decimalPlaces;
            const previewDiffs = previewRules ? (() => {
              const diffs: string[] = [];
              if (previewRules.reductionType && previewRules.reductionType !== bidConfig.reductionType) diffs.push('tipo');
              if (previewRules.variacaoMinima != null && Number(previewRules.variacaoMinima) !== Number(bidConfig.reductionMin)) diffs.push('min');
              if (previewRules.casasDecimais != null && Number(previewRules.casasDecimais) !== Number(bidConfig.decimalPlaces)) diffs.push('casas');
              return diffs;
            })() : [];
            return (
              <div className="bg-central-elevado rounded-lg border border-central-borda p-4">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Exigências do portal</span>
                    <span className="text-xs text-slate-500">(prévia — antes de iniciar)</span>
                  </h4>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handlePreviewPortal}
                      disabled={previewLoading}
                      className="text-xs px-2 py-1 rounded bg-blue-500/15 border border-blue-700/40 text-blue-300 hover:bg-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                      title="Lê do portal as regras reais (variação mínima, tipo, modo, casas decimais). Requer sessão pública aberta."
                    >
                      {previewLoading ? '⏳ Buscando…' : '🔄 Buscar do portal'}
                    </button>
                  </div>
                </div>
                {!previewRules && (
                  <>
                    <div className="text-xs flex justify-between">
                      <span className="text-slate-500">Casas decimais (padrão {portalLabel}):</span>
                      <span className="font-mono font-semibold text-emerald-300">{dec}</span>
                    </div>
                    {previewError && (
                      <div className="mt-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-700/40 rounded px-2 py-1.5">
                        ⚠ {previewError}
                      </div>
                    )}
                    {!previewError && (
                      <div className="col-span-2 flex items-center gap-1.5 text-blue-300 pt-2 text-[11px]">
                        <span>ℹ Clique em <b>Buscar do portal</b> pra ler as regras reais agora, ou serão lidas automaticamente ao <b>Iniciar</b> a disputa.</span>
                      </div>
                    )}
                  </>
                )}
                {previewRules && (
                  <div className="space-y-1.5">
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <span className="text-slate-500">Tipo de variação:</span>
                      <span className={`font-mono font-semibold text-right ${previewDiffs.includes('tipo') ? 'text-amber-300' : 'text-emerald-300'}`}>
                        {previewRules.tipoVariacao === 'V' ? 'Valor (R$)' : previewRules.tipoVariacao === 'P' ? 'Percentual (%)' : '—'}
                        {previewDiffs.includes('tipo') && <span className="text-slate-500 font-normal ml-1">(atual: {bidConfig.reductionType === 'fixed' ? 'Valor' : 'Percentual'})</span>}
                      </span>
                      <span className="text-slate-500">Variação mínima:</span>
                      <span className={`font-mono font-semibold text-right ${previewDiffs.includes('min') ? 'text-amber-300' : 'text-emerald-300'}`}>
                        {previewRules.variacaoMinima != null
                          ? (previewRules.tipoVariacao === 'V'
                              // `minimumFractionDigits: 2` sozinho deixa o MÁXIMO em 3 (padrão do
                              // Intl), então 0,0001 arredondava e aparecia "R$ 0,00" — com o
                              // "✓ config bate com o portal" logo abaixo, porque a comparação usa
                              // o número cru e está certa. Tela dizendo zero e "confere" ao mesmo
                              // tempo (INCA PE 354/2026, 15/09/2026). O portal manda as casas junto;
                              // na falta delas, 4 é o padrão do ComprasNet.
                              ? `R$ ${previewRules.variacaoMinima.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: Math.max(2, Number(previewRules.casasDecimais) || 4),
                                })}`
                              : `${previewRules.variacaoMinima}%`)
                          : '—'}
                        {previewDiffs.includes('min') && <span className="text-slate-500 font-normal ml-1">(atual: {bidConfig.reductionMin})</span>}
                      </span>
                      {previewRules.casasDecimais != null && (
                        <>
                          <span className="text-slate-500">Casas decimais:</span>
                          <span className={`font-mono font-semibold text-right ${previewDiffs.includes('casas') ? 'text-amber-300' : 'text-emerald-300'}`}>
                            {previewRules.casasDecimais}
                            {previewDiffs.includes('casas') && <span className="text-slate-500 font-normal ml-1">(atual: {bidConfig.decimalPlaces})</span>}
                          </span>
                        </>
                      )}
                      {previewRules.modoDisputa && (
                        <>
                          <span className="text-slate-500">Modo de disputa:</span>
                          <span className="font-mono font-semibold text-right text-slate-300">{(() => {
                            // ModoDisputaCompraEnum oficial (HAR 27/05/26):
                            //   A=Aberto, F=Fechado, AF=Aberto/Fechado, FA=Fechado/Aberto, D=Dispensa Comum
                            const md = previewRules.modoDisputa;
                            if (md === 'A') return 'Aberto';
                            if (md === 'F') return 'Fechado';
                            if (md === 'AF') return 'Aberto-Fechado';
                            if (md === 'FA') return 'Fechado-Aberto';
                            if (md === 'D') return 'Dispensa Comum';
                            return md;
                          })()}</span>
                        </>
                      )}
                      {previewRules.tempoIntencoesRecurso != null && (
                        <>
                          <span className="text-slate-500">Tempo de recurso:</span>
                          <span className="font-mono font-semibold text-right text-slate-300">{previewRules.tempoIntencoesRecurso} min</span>
                        </>
                      )}
                    </div>
                    {previewDiffs.length > 0 && (
                      <button
                        type="button"
                        onClick={applyPreviewRules}
                        className="w-full mt-2 text-xs px-3 py-1.5 rounded bg-emerald-500/20 border border-emerald-700/50 text-emerald-300 hover:bg-emerald-500/30 font-medium"
                      >
                        Aplicar {previewDiffs.length} alteraç{previewDiffs.length === 1 ? 'ão' : 'ões'} do portal
                      </button>
                    )}
                    {previewDiffs.length === 0 && (
                      <div className="text-[11px] text-emerald-400 text-center pt-1">✓ Config atual já bate com o portal</div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {/* R1.11: Regras do Portal — herdadas do /participacao do ComprasNet pra contexto */}
          {(() => {
            const part = session?.rankingData?.participacao;
            if (!part) return null;
            const cfg = part.configuracao || {};
            const isPregao = Number(part.modalidade) === 5;
            const isDispensa = Number(part.modalidade) === 6 || Number(part.modalidade) === 8;
            // Detecção AF via helper unificado (formatters.isModoAbertoFechado).
            const isAbertoFechado = isModoAbertoFechado(
              { modoDisputa: part.modoDisputa, modoDisputaNome: part.modoDisputaNome },
              session?.rankingData,
            );
            // Sugestões automáticas baseadas nas regras
            const suggested: Record<string, any> = {};
            // Casas decimais: olhar se item tem `quantidadeMaximaCasasDecimaisValor` (default 4 pra unitário, 2 pra total)
            const itensRows = (() => {
              const ig = session?.rankingData?.itensGrupo?.['-1'];
              if (Array.isArray(ig)) return ig;
              return ig?.registros || ig?.itens || ig?.items || [];
            })();
            const hasUnitDispute = itensRows.some((i: any) => i.disputaPorValorUnitario);
            if (hasUnitDispute) suggested.decimalPlaces = 4;
            // Sniping só faz sentido em dispensa modo D (Comum) ou pregão modo F (Aberto-Fechado fase final)
            if (isDispensa && String(part.modoDisputa) === 'D') suggested.dispensaLastSeconds = true;
            // Start mode aberto no pregão A, aguardar 1º lance em A-F
            if (isAbertoFechado) suggested.startMode = 'aguardar_primeiro';
            // R1.11: Variação mínima entre lances vem do detalhamentoItem.variacaoMinimaEntreLances (em %)
            const detItens = session?.rankingData?.detalhamentoItem || {};
            const variacoes = itensRows
              .map((i: any) => {
                const det = detItens[String(i.numero)];
                if (!det) return null;
                return { val: det.variacaoMinimaEntreLances, tipo: det.tipoVariacaoMinimaEntreLances };
              })
              .filter((v: any) => v && v.val != null);
            const variacaoMinPortal = variacoes.length ? Math.max(...variacoes.map((v: any) => Number(v.val))) : null;
            const tipoVariacao = variacoes.length ? variacoes[0].tipo : null; // 'P'=Percentual, 'V'=Valor absoluto
            // Sugere reductionType + reductionMin baseado nas regras do portal
            if (tipoVariacao === 'P') {
              suggested.reductionType = 'percentage';
              if (variacaoMinPortal != null) suggested.reductionMin = variacaoMinPortal;
            } else if (tipoVariacao === 'V') {
              suggested.reductionType = 'fixed';
              if (variacaoMinPortal != null) suggested.reductionMin = variacaoMinPortal;
            }
            const hasSuggestions = Object.keys(suggested).length > 0;
            const allMatch = Object.entries(suggested).every(([k, v]) => (bidConfig as any)[k] === v);

            return (
              <div className="bg-central-elevado rounded-lg border border-central-borda p-4">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-slate-500">Regras do Portal</span>
                    <span className="text-xs text-slate-500">(do ComprasNet)</span>
                  </h4>
                  {hasSuggestions && !allMatch && (
                    <button
                      type="button"
                      onClick={() => setBidConfig({ ...bidConfig, ...suggested })}
                      className="text-xs px-2 py-1 rounded bg-emerald-500/15 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-500/25"
                      title="Aplica os valores sugeridos pelas regras do portal nesta configuração"
                    >
                      Aplicar sugestões ({Object.keys(suggested).length})
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {cfg.qtdeMaximaItensEmDisputa != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Qtde máx. itens simultâneos:</span>
                      <span className="text-slate-200 font-mono">{cfg.qtdeMaximaItensEmDisputa}</span>
                    </div>
                  )}
                  {cfg.tempoAvisoPrevioAberturaItens != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Aviso prévio abertura:</span>
                      <span className="text-slate-200 font-mono">{cfg.tempoAvisoPrevioAberturaItens} min</span>
                    </div>
                  )}
                  {cfg.inicioPeriodoAberturaItem && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Janela abertura itens:</span>
                      <span className="text-slate-200 font-mono">{cfg.inicioPeriodoAberturaItem}{cfg.fimPeriodoAberturaItem ? `–${cfg.fimPeriodoAberturaItem}` : ''}</span>
                    </div>
                  )}
                  {cfg.tempoIntencoesRecurso != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tempo intenção recurso:</span>
                      <span className="text-slate-200 font-mono">{cfg.tempoIntencoesRecurso} min</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Modalidade:</span>
                    <span className="text-slate-200 font-mono">{isPregao ? 'Pregão Eletrônico' : isDispensa ? 'Dispensa Eletrônica' : '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Modo disputa:</span>
                    {/* F-5 (Onda 2): enum OFICIAL (HAR 27/05) — 'F' é FECHADO; o rótulo
                        antigo dizia "Aberto-Fechado" pra modo F e nem tinha case AF. */}
                    <span className="text-slate-200 font-mono">{({ A: 'Aberto', F: 'Fechado', AF: 'Aberto/Fechado', FA: 'Fechado/Aberto', D: 'Comum (Dispensa)' } as Record<string, string>)[part.modoDisputa] || part.modoDisputa}</span>
                  </div>
                  {variacaoMinPortal != null && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Variação mín. entre lances:</span>
                      <span className="text-emerald-300 font-mono font-semibold">
                        {tipoVariacao === 'P' ? `${variacaoMinPortal}%` : `R$ ${variacaoMinPortal}`}
                      </span>
                    </div>
                  )}
                  {hasUnitDispute && (
                    <div className="col-span-2 flex items-center gap-1.5 text-blue-300 pt-1">
                      <span>ℹ Disputa por valor unitário — sugestão: 4 casas decimais</span>
                    </div>
                  )}
                  {variacaoMinPortal != null && (
                    <div className="col-span-2 flex items-center gap-1.5 text-emerald-300 pt-1 text-[11px]">
                      <span>✓ Variação mínima já está nos itens (do endpoint /detalhamento) — não precisa abrir o ComprasNet</span>
                    </div>
                  )}
                  {isAbertoFechado && (
                    <div className="col-span-2 flex items-center gap-1.5 text-amber-300 pt-1">
                      <span>⚠ Modo Aberto-Fechado: fase final só pra top 3 (envelope fechado)</span>
                    </div>
                  )}
                  {isDispensa && String(part.modoDisputa) === 'D' && (
                    <div className="col-span-2 flex items-center gap-1.5 text-blue-300 pt-1">
                      <span>ℹ Dispensa Comum: sniping nos últimos segundos é a estratégia padrão</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* AÇÃO + VELOCIDADE (refactor 29/05/2026): substitui os 3 toggles
              (autoBid/fastMode/dryRun) por 2 escolhas claras. autoBid é sempre
              true (iniciar = robô roda); Simular/Real = dryRun (vale pra TODOS
              os portais); Normal/Rápido = fastMode (só ComprasNet, que tem
              extension client-side — BLL/PCP são backend HTTP). */}
          <div className={`rounded-lg border p-4 ${bidConfig.dryRun === false ? 'bg-red-500/10 border-red-700/50' : 'bg-amber-500/10 border-amber-700/50'}`}>
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Como o robô vai agir</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBidConfig({ ...bidConfig, autoBid: true, dryRun: true })}
                className={`rounded-lg px-3 py-2.5 text-sm font-medium border text-left transition ${bidConfig.dryRun !== false ? 'bg-amber-500/20 border-amber-500 text-amber-200' : 'bg-central-cartao border-central-borda text-slate-400 hover:border-central-borda'}`}
              >
                🧪 Simular
                <span className="block text-[10px] font-normal opacity-80 mt-0.5">só loga "lançaria X" — não envia</span>
              </button>
              <button
                type="button"
                onClick={async () => {
                  // A confirmação mais crítica do módulo: daqui em diante o robô
                  // manda lance de verdade, e lance enviado não tem rollback.
                  const ok = await confirm({
                    title: 'Ligar LANCE REAL?',
                    message: 'O robô passa a enviar lances de verdade no portal, cobrindo o melhor lance até o piso configurado.',
                    consequence: 'Lance enviado ao portal NÃO tem rollback. Confira os pisos por item antes de confirmar.',
                    danger: true,
                    confirmLabel: 'Ligar lance real',
                  });
                  if (!ok) return;
                  setBidConfig({ ...bidConfig, autoBid: true, dryRun: false });
                }}
                className={`rounded-lg px-3 py-2.5 text-sm font-medium border text-left transition ${bidConfig.dryRun === false ? 'bg-red-500/25 border-red-500 text-red-200' : 'bg-central-cartao border-central-borda text-slate-400 hover:border-central-borda'}`}
              >
                🤖 Lance REAL
                <span className="block text-[10px] font-normal opacity-80 mt-0.5">envia de verdade no portal</span>
              </button>
            </div>

            {isComprasNet ? (
              <div className="mt-3 pt-3 border-t border-central-borda">
                {/* MOTOR (03/09/2026, Fuzileiros s124): "Servidor" = poll do
                    em-disputa, decisão e lance no backend, sem aba do Firefox e
                    sem a trava de uma ComprasNet por vez. A extensão continua
                    disponível (Normal/Rápido) até o motor servidor ser provado
                    em disputa real. */}
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Motor</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setBidConfig({ ...bidConfig, engine: 'servidor', fastMode: false })}
                    className={`rounded-lg px-3 py-2 text-sm border text-left transition ${bidConfig.engine === 'servidor' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-200' : 'bg-central-cartao border-central-borda text-slate-400 hover:border-central-borda'}`}
                  >
                    🖥️ Servidor <span className="block text-[10px] font-normal opacity-80">sem aba · ~50ms · roda em paralelo</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBidConfig({ ...bidConfig, engine: 'extensao', fastMode: false })}
                    className={`rounded-lg px-3 py-2 text-sm border text-left transition ${bidConfig.engine !== 'servidor' && !bidConfig.fastMode ? 'bg-blue-500/20 border-blue-500 text-blue-200' : 'bg-central-cartao border-central-borda text-slate-400 hover:border-central-borda'}`}
                  >
                    Extensão <span className="block text-[10px] font-normal opacity-80">~900ms (decisão no backend)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBidConfig({ ...bidConfig, engine: 'extensao', fastMode: true })}
                    className={`rounded-lg px-3 py-2 text-sm border text-left transition ${bidConfig.engine !== 'servidor' && bidConfig.fastMode ? 'bg-purple-500/20 border-purple-500 text-purple-200' : 'bg-central-cartao border-central-borda text-slate-400 hover:border-central-borda'}`}
                  >
                    ⚡ Extensão rápida <span className="block text-[10px] font-normal opacity-80">~200ms (decisão na aba)</span>
                  </button>
                </div>
                {bidConfig.engine === 'servidor' ? (
                  <div className="mt-2 text-[10px] text-slate-500 leading-snug">
                    🖥️ <b>Motor servidor:</b> relê a disputa a cada <b>3s</b>, <b>1s nos últimos 120s</b> e
                    <b> 500ms nos últimos 30s</b>; o lance sai direto do backend (31–63ms medidos). Não usa o
                    Firefox: pode rodar junto com outra disputa. Salvar aqui vale no próximo ciclo.
                  </div>
                ) : (
                  <div className="mt-2 text-[10px] text-slate-500 leading-snug">
                    ⏱️ <b>Detecção do lance do concorrente:</b> o robô relê a disputa a cada <b>3s</b> e
                    acelera pra <b>500ms nos últimos 120s</b> (reação rápida só na reta final). Fora dos
                    120s, cobrir um undercut leva ~3–6s. Usa uma aba do Firefox: só uma disputa por vez.
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-2 text-[11px] text-slate-500">Decisão no backend (HTTP). "Rápido" só se aplica ao ComprasNet.</div>
            )}

            <div className="mt-3 text-[11px] text-slate-400">
              {bidConfig.dryRun === false
                ? '⚠️ Modo REAL: ao Iniciar, o robô envia lances de verdade.'
                : '🧪 Modo simulação: o robô decide e loga "lançaria X", mas NÃO envia. Escolha "Lance REAL" quando quiser valer.'}
            </div>

            {/* LANCE REAL LIGADO DENTRO DA JANELA DE SNIPING (28/08/2026, Dispensa
                18/2026 MAPA): com sniping ativo o robô segura os lances até a reta
                final, MAS antes disso manda um "lance-âncora" (meu valor − variação,
                ≥ piso) só pra provar que o canal funciona — JWT, fase, casas decimais.
                Foi criado depois do CRMV s80, onde os 422 de configuração só
                apareceram nos últimos 120s, sem tempo de reagir.
                O âncora só dispara FORA da janela. Ligando o modo real já dentro
                dela, ele nunca roda e a disputa entra na reta final sem nenhuma
                prova de que o lance sai — foi o que aconteceu no MAPA (real ligado
                a 34s do fim). Aqui isso é dito na hora, que é quando dá pra agir. */}
            {(() => {
              if (bidConfig.dryRun !== false || bidConfig.dispensaLastSeconds !== true) return null;
              const janelaS = Number(bidConfig.dispensaLastSecondsValue);
              if (!isFinite(janelaS) || janelaS <= 0) return null;
              // Menor fim FUTURO entre os itens; fallback no fim da sessão. Não
              // precisa da precisão do countdown do header — só de saber se já
              // estamos na reta final.
              const cands: number[] = [];
              for (const lim of (session?.itemLimits || []) as any[]) {
                const ts = lim?.fimContagem ? new Date(lim.fimContagem).getTime() : NaN;
                if (isFinite(ts)) cands.push(ts);
              }
              const fimSessao = session?.disputeEndTime ? new Date(session.disputeEndTime).getTime() : NaN;
              if (isFinite(fimSessao)) cands.push(fimSessao);
              if (cands.length === 0) return null;
              const futuros = cands.filter((t) => t > Date.now());
              // Sem fim futuro, a disputa já encerrou — nada a avisar.
              if (futuros.length === 0) return null;
              const restanteS = Math.round((Math.min(...futuros) - Date.now()) / 1000);
              if (restanteS > janelaS) return null;
              return (
                <div className="mt-2 rounded-md border border-amber-600/40 bg-amber-500/10 px-2.5 py-2 text-[11px] text-amber-200 leading-snug">
                  ⏱️ <b>Faltam ~{restanteS}s e a janela de sniping é de {janelaS}s</b> — você já está na reta final.
                  O <b>lance-âncora não vai rodar</b> (ele só dispara antes da janela), então o primeiro lance
                  de verdade será dentro dos segundos finais, sem ter validado o canal antes.
                  Numa próxima, ligue o modo real desde o play: o sniping continua segurando os lances,
                  e o âncora expõe um erro de configuração com horas de sobra.
                </div>
              );
            })()}
          </div>

          {/* Strategies */}
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-3">Estratégias de Lance</h4>
            <div className="space-y-3">
              {/* Modelo unificado (29/05/2026): "Melhor posição" e "Lance do valor
                  limite" viraram implícitos na Posição-alvo (top-N=auto cobre o
                  melhor; se nada cabe no piso, lança no piso). Restam o alvo + o
                  cobrir-próprio (ortogonal). bestPosition/bidLimitValue são
                  forçados true no save (compat extension). */}
              <ToggleOption
                label="Cobrir próprio lance"
                description="Re-cobrir o próprio lance mesmo já liderando (melhora posição nos segundos finais)"
                value={strategies.coverOwnBid}
                onChange={(v) => setStrategies({ ...strategies, coverOwnBid: v })}
              />

              {/* Item julgado por MAIOR DESCONTO: o lance vai em PERCENTUAL, não
                  em reais. O backend converte nas bordas (helpers/lance-desconto.util)
                  e se RECUSA a lançar sozinho enquanto `descontoAutoBid` não for
                  true — o intervalo mínimo entre lances em item de desconto é
                  validado só no servidor do ComprasNet e nunca foi visto ao vivo.
                  Até 24/09/2026 esse campo não tinha controle na tela: a trava
                  existia e não havia como destravar sem mexer no banco.
                  Só aparece quando a disputa TEM item de desconto — noutra sessão
                  seria só ruído, e opção que não faz nada ensina a ignorar. */}
              {editedLimits.some((it: any) => it?.priceMode === 'desconto') && (
                <div className="border-t border-central-borda pt-3">
                  <ToggleOption
                    label="Auto-lance em item por MAIOR DESCONTO"
                    description={
                      bidConfig.descontoAutoBid
                        ? 'Liberado: o robô converte o preço em percentual e envia. A conversão usa o valor estimado como base fixa, igual ao portal.'
                        : 'Travado: o robô acompanha e decide, mas NÃO envia lance nos itens de desconto — registra o bloqueio na timeline. Libere só depois de ver uma recusa real do portal e entender como ele conta o intervalo mínimo (em pontos de desconto ou sobre o preço).'
                    }
                    value={bidConfig.descontoAutoBid === true}
                    onChange={(v) => setBidConfig({ ...bidConfig, descontoAutoBid: v })}
                  />
                </div>
              )}
              <div className="border-t border-central-borda pt-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm text-slate-300 font-medium">
                      Posição-alvo (top-N)
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <strong className="text-slate-300">0 = auto</strong> (melhor posição alcançável dentro do piso, do 1º pra baixo). <strong className="text-slate-300">N ≥ 1</strong> = mira o N-ésimo, escalona pra baixo se o piso impede. <strong className="text-slate-300">Se nada couber acima do piso, lança no piso.</strong> Útil em Dispensa (top-3 herda por desclassificação) e Pregão Aberto/Fechado (top-3 vão pra fase fechada).
                    </div>
                  </div>
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={(strategies as any).coverTopN || 0}
                    onChange={(e) => setStrategies({ ...strategies, coverTopN: Number(e.target.value) || 0 })}
                    className={`w-16 rounded px-2 py-1 text-sm font-mono text-center ${campo}`}
                    title="0 = auto (melhor alcançável). 2 = força mínimo 2º (escalona pra baixo se piso impede). 3 = top-3 (recomendado pra Aberto/Fechado)."
                  />
                </div>
              </div>
            </div>
          </div>

          {/*
            ⛔ ESTRUTURA DE GRUPO DESCONHECIDA — o alerta que teria evitado o
            PE 22/2026 COREN-PE (20/08).

            Vários itens e NENHUM com grupo é ambíguo: pode ser pregão por item
            (legítimo) ou um GRUPO cuja informação nunca chegou. O PNCP não expõe
            agrupamento — medido — então proposta semeada dele nasce assim, e só
            o "Sincronizar do Portal" ou a IA do edital preenchem.

            Se for grupo e ninguém sincronizar, o robô avalia item a item, o piso
            de cada um bloqueia o lance que o TOTAL comporta, e ele NÃO LANÇA
            NADA numa disputa ganhável. Foi exatamente o que aconteceu: 9 itens,
            piso somado R$ 133.900 contra líder em R$ 139.360, zero lances.
          */}
          {unidadesDaSessao > 1 && (session.itemLimits || []).every((il: any) => il.groupNumber == null && il.groupIdentificador == null) && (
            <div className="rounded-lg border border-amber-700/50 bg-amber-500/5 px-3 py-2.5">
              <div className="text-sm font-semibold text-amber-300 mb-1">
                ⚠ Estrutura de grupo desconhecida
              </div>
              <p className="text-[12px] text-amber-200/80 leading-snug">
                Os {(session.itemLimits || []).length} itens estão sem informação de grupo. Se o edital
                for julgado <strong>por grupo</strong> (pelo total), o robô vai disputar item a item —
                e o piso de cada item impede o lance que o total comportaria, ou seja, ele não lança.
              </p>
              <p className="mt-1.5 text-[12px] text-amber-200/80 leading-snug">
                {isBLL
                  ? 'Confirme no portal se os itens formam um lote. Durante a disputa, se o portal informar o agrupamento, o HUB corrige sozinho.'
                  : 'Use "Sincronizar do Portal" abaixo antes de iniciar — é de lá que vem o agrupamento (o PNCP não informa).'}
              </p>
            </div>
          )}

          {/*
            Julgamento dos lotes. Só aparece com mais de uma unidade, porque com
            um lote só a pergunta não existe.

            O portal NÃO expõe isso e o critério do PNCP ("Menor preço") não
            distingue. No PE 021/2026 de Nova Olímpia a BLL mostrava 3 lotes com
            um 1º colocado cada e o edital julgava pela SOMA: a IPCOM ganhou o
            lote 1 e ficou em 2º na licitação (R$ 9.540 contra R$ 7.200), com a
            pregoeira somando na mão no chat. Marcar "global" é o que autoriza o
            HUB a fazer essa conta.

            Fica aqui, e não só no cadastro, porque é aqui que se opera a disputa
            e onde o piso somado passa a fazer sentido.
          */}
          {unidadesDaSessao > 1 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Julgamento dos lotes</h4>
              <select
                value={julgamentoAgrupamento}
                onChange={(e) => {
                  const v = e.target.value === 'global' ? 'global' : null;
                  setJulgamentoAgrupamento(v ? 'global' : 'por-lote');
                  if (session.itemId) {
                    procurementService
                      .updateItemDetails(session.itemId, { julgamentoAgrupamento: v })
                      .catch(() => {});
                  }
                }}
                className={`w-full rounded-lg px-3 py-2 text-sm outline-none transition-colors ${campo}`}
              >
                <option value="por-lote">Por lote — cada lote decide o seu</option>
                <option value="global">Global — a classificação é a SOMA dos {unidadesDaSessao} lotes</option>
              </select>
              <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                {julgamentoAgrupamento === 'global'
                  ? 'Ao coletar o resultado, o HUB soma o lance de cada fornecedor nos lotes (valor × quantidade) e apura a classificação real da licitação — a conta que o portal não faz.'
                  : 'O portal mostra um 1º colocado por lote. Se o edital disser "menor preço global", marque Global: dá pra ganhar um lote e mesmo assim perder a licitação.'}
              </p>
            </div>
          )}

          {/* Valor limite (piso) por item — editável; mudar em curso confirma no pai */}
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
              Valor limite (piso) por item
              {(session.status === 'ativo' || session.status === 'aguardando') && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40">disputa em curso — confirma ao salvar</span>
              )}
              {!isBLL && (
                <button
                  type="button"
                  onClick={handleScrapeItems}
                  disabled={scrapingItems}
                  className="ml-auto text-[11px] px-2 py-1 rounded-lg border border-central-borda bg-central-cartao hover:bg-central-elevado text-central-texto disabled:opacity-60 flex items-center gap-1"
                  title="Puxa os itens e seus valores registrados no portal (para disputas cadastradas manualmente)"
                >
                  {scrapingItems ? '⏳ Buscando itens…' : '🔄 Buscar itens do portal'}
                </button>
              )}
            </h4>
            {scrapeItemsError && (
              <p className="text-xs text-amber-400 mb-2">⚠ {scrapeItemsError}</p>
            )}
            <div className="space-y-3">
              {agruparPorUnidade(editedLimits).map((un) => {
                const fmtU = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                const pisoUn = somaUnidade(un.membros, 'limitValue');
                const melhorUn = somaUnidade(un.membros, 'bestBid', session.itemLimits || [], isPcpGlobal);
                // Global: basta UM item com o valor do lote; por item: todos precisam ter.
                const temMelhor = (isPcpGlobal || un.membros.every(({ it }) =>
                  Number((session.itemLimits || []).find((l: any) => l.itemNumber === it.itemNumber)?.bestBid) > 0,
                )) && melhorUn > 0;
                const cobreUn = temMelhor && pisoUn > 0 && pisoUn <= melhorUn;
                return (
                  <div key={un.chave} className={un.ehGrupo ? 'rounded-lg border border-central-borda bg-central-cartao p-2' : ''}>
                    {/* Cabeçalho da unidade: no grupo é AQUI que a cobertura é decidida
                        (julgamento pelo total). Item solto continua se resolvendo sozinho. */}
                    {un.ehGrupo && (
                      <div className="flex items-center gap-2 mb-2 px-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-300">{un.rotulo}</span>
                        <span className="text-[10px] text-slate-500">{un.membros.length} {un.membros.length === 1 ? 'item' : 'itens'} · julgado pelo TOTAL</span>
                        <span className="ml-auto text-[10px] text-slate-400">Σ piso R$ {fmtU(pisoUn)}</span>
                        {temMelhor && (
                          <span
                            className={`text-[10px] whitespace-nowrap ${cobreUn ? 'text-emerald-400' : 'text-amber-400'}`}
                            title={cobreUn
                              ? `Σ dos seus pisos (R$ ${fmtU(pisoUn)}) ≤ total do melhor (R$ ${fmtU(melhorUn)}) — o robô cobre o grupo`
                              : `Σ dos seus pisos (R$ ${fmtU(pisoUn)}) está ACIMA do total do melhor (R$ ${fmtU(melhorUn)}) — o robô não cobre o grupo`}
                          >
                            {cobreUn ? '✓ cobre o grupo' : '⚠ acima do melhor'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      {un.membros.map(({ it, idx }) => (
                        <div key={it.itemNumber ?? idx} className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 flex-1 truncate" title={it.description}>
                            {it.itemNumber}. {it.description || 'Item'}
                          </span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">R$</span>
                            {/* ⛔ type="text", não "number". Num campo numérico o
                                navegador trata a VÍRGULA como conteúdo inválido e
                                devolve string vazia — e o texto digitado fica na
                                tela, porque o React já tem '' no value e não toca
                                no DOM. O operador via "40240,8" e o estado guardava
                                null. TRT-8 PE 90009/2026 (24/09/2026): três dos
                                quatro pisos nunca entraram e o robô disputou REAL
                                com R$ 5.230,80 a menos de piso do que ele pensava. */}
                            <input
                              type="text" inputMode="decimal"
                              value={rascunhoPiso[idx] ?? (it.limitValue ?? '')}
                              onChange={(e) => {
                                const bruto = e.target.value;
                                setRascunhoPiso((r) => ({ ...r, [idx]: bruto }));
                                const n = parseDecimalBr(bruto);
                                // Texto ilegível NÃO apaga o piso que já valia: o
                                // campo fica vermelho e o valor anterior continua de
                                // pé. Piso nulo autoriza o robô a ir ao chão.
                                if (n !== null || bruto.trim() === '') {
                                  setEditedLimits(editedLimits.map((x, i) => i === idx ? { ...x, limitValue: n } : x));
                                }
                              }}
                              onBlur={() => setRascunhoPiso((r) => { const c = { ...r }; delete c[idx]; return c; })}
                              className={`w-28 rounded-lg px-2 py-1 text-sm text-right ${campoComEstado(
                                rascunhoPiso[idx] !== undefined && rascunhoPiso[idx].trim() !== '' && parseDecimalBr(rascunhoPiso[idx]) === null
                                  && 'border-red-500'
                              )}`}
                            />
                            {/* Em item por MAIOR DESCONTO o piso continua em reais (o
                                motor opera no preço derivado), mas quem opera pensa em
                                percentual. Os dois lado a lado: um piso que não entrou
                                salta aos olhos — no TRT-8 três itens teriam mostrado
                                33%, 19% e 28% onde o operador queria 10%. */}
                            {(() => {
                              const pct = descontoEquivalente(it.limitValue, (it as any).estimatedPrice);
                              if (pct === null) return null;
                              return (
                                <span className="text-[11px] text-slate-400 w-16 text-right tabular-nums" title="Desconto que este piso representa sobre o valor estimado">
                                  {pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
                                </span>
                              );
                            })()}
                          </div>
                          {/* Cobertura POR ITEM só faz sentido em item SOLTO. Num grupo o
                              item sozinho não ganha nem perde nada — quem decide é o total,
                              e mostrar "⚠ acima" por item ali só assusta sem informar. */}
                          {(() => {
                            if (un.ehGrupo) return <span className="w-16" />;
                            // PCP global sem groupNumber: bestBid é o total do lote, piso é unitário — não compara.
                            if (isPcpGlobal) return null;
                            const best = Number((session.itemLimits || []).find((l: any) => l.itemNumber === it.itemNumber)?.bestBid) || 0;
                            const floor = Number(it.limitValue) || 0;
                            if (!(best > 0) || !(floor > 0)) return null;
                            const cobre = floor <= best;
                            return (
                              <span
                                className={`text-[10px] whitespace-nowrap w-16 text-right ${cobre ? 'text-emerald-400' : 'text-amber-400'}`}
                                title={cobre ? `Seu piso ≤ melhor do mercado (R$ ${best.toFixed(4)}) — o robô cobre este item` : `Seu piso está ACIMA do melhor (R$ ${best.toFixed(4)}) — o robô não cobre este item`}
                              >
                                {cobre ? '✓ cobre' : '⚠ acima'}
                              </span>
                            );
                          })()}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {editedLimits.length === 0 && <p className="text-xs text-slate-500">Sem itens configurados. Clique em <strong className="text-indigo-300">Buscar itens do portal</strong> pra puxar os itens e seus valores (disputas cadastradas manualmente).</p>}
            </div>
            {/* Fechamento POR UNIDADE. Antes havia só dois caminhos — "tudo grupo" ou
                "tudo item" — e o misto (grupo + item solto) caía no segundo: os itens
                do grupo eram contados como se cada um se vencesse sozinho, e a Σ de
                pisos de unidades diferentes virava um número que não significa nada.
                Agora cada unidade é fechada com o critério do portal, e o total geral
                só aparece quando existe UMA unidade (onde ele de fato decide). */}
            {editedLimits.length > 0 && (() => {
              const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const limits = session.itemLimits || [];
              const unidades = agruparPorUnidade(editedLimits);

              // ⛔ Σ dos melhores POR ITEM é um concorrente que NÃO EXISTE.
              // Num grupo julgado pelo TOTAL, nenhum fornecedor precisa ter o
              // melhor de cada item ao mesmo tempo — e normalmente não tem.
              // TRT-8 PE 90009/2026 (24/09/2026): a soma dos melhores por item
              // dava R$ 170.798,52 e o melhor TOTAL de verdade era R$ 201.324,69,
              // do portal. A tela dizia "R$ 58.665,48 acima" quando a distância
              // real era R$ 32.542,94 — quase o dobro. Aqui não mudou a decisão
              // (o piso não cobria nem um nem outro), mas num caso apertado faz
              // desistir de disputa que dava para ganhar.
              //
              // O nó de grupo do `emDisputa` traz o total agregado pelo PRÓPRIO
              // portal, que é a mesma fonte do MELHOR GERAL do cabeçalho.
              const melhorTotalDoPortal = (() => {
                const rows = (session as any)?.rankingData?.emDisputa;
                if (!Array.isArray(rows)) return null;
                const g = rows.find((r: any) => r?.tipo === 'G' || r?.numero === -1);
                const bruto = g?.melhorValorGeral?.valorCalculado ?? g?.melhorValorGeral?.valorInformado ?? g?.melhorValorGeral;
                const n = Number(bruto);
                return Number.isFinite(n) && n > 0 ? n : null;
              })();

              const fechamentos = unidades.map((un) => {
                const piso = somaUnidade(un.membros, 'limitValue');
                const somaDosItens = somaUnidade(un.membros, 'bestBid', limits, isPcpGlobal);
                // Só o grupo tem "total do portal"; item solto continua como era.
                const doPortal = un.ehGrupo && unidades.length === 1 ? melhorTotalDoPortal : null;
                const melhor = doPortal ?? somaDosItens;
                const temMelhor = doPortal !== null || ((isPcpGlobal || un.membros.every(({ it }) =>
                  Number(limits.find((l: any) => l.itemNumber === it.itemNumber)?.bestBid) > 0,
                )) && somaDosItens > 0);
                return { un, piso, melhor, temMelhor, doPortal, cobre: temMelhor && piso > 0 && piso <= melhor };
              });
              const comMelhor = fechamentos.filter((f) => f.temMelhor);
              const cobrindo = comMelhor.filter((f) => f.cobre).length;
              const somaGeral = fechamentos.reduce((s, f) => s + f.piso, 0);
              const unicaUnidade = unidades.length === 1;
              return (
                <div className="mt-2 rounded-lg bg-central-cartao/70 border border-central-borda px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">
                        {unicaUnidade
                          ? (unidades[0].ehGrupo ? 'Total corrigido (Σ piso × qtd)' : 'Piso × qtd')
                          : `Σ dos pisos das ${unidades.length} unidades — informativo`}
                      </div>
                      <div className="text-base font-bold text-white">R$ {fmt(somaGeral)}</div>
                    </div>
                    {comMelhor.length > 0 ? (
                      <div className="text-right">
                        {unicaUnidade ? (
                          <>
                            <div className="text-[10px] text-slate-400" title={comMelhor[0].doPortal != null
                              ? 'Total do melhor concorrente, agregado pelo próprio portal'
                              : 'Soma dos melhores POR ITEM — pode não corresponder a nenhum concorrente real'}>
                              melhor {unidades[0].ehGrupo ? 'geral' : 'do item'} R$ {fmt(comMelhor[0].melhor)}
                              {unidades[0].ehGrupo && comMelhor[0].doPortal == null && <span className="text-slate-500"> (Σ por item)</span>}
                            </div>
                            <div className={`text-xs font-semibold ${comMelhor[0].cobre ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {comMelhor[0].cobre
                                ? `✓ R$ ${fmt(comMelhor[0].melhor - comMelhor[0].piso)} abaixo — pode vencer`
                                : `⚠ R$ ${fmt(comMelhor[0].piso - comMelhor[0].melhor)} acima — não cobre`}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`text-xs font-semibold ${cobrindo === comMelhor.length ? 'text-emerald-400' : 'text-amber-400'}`}>
                              Cobre {cobrindo} de {comMelhor.length} {comMelhor.length === 1 ? 'unidade' : 'unidades'}
                            </div>
                            <div className="text-[10px] text-slate-500">cada grupo/item é disputado separado</div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-500 text-right">ranking ainda não coletado<br/>(sem melhor pra comparar)</div>
                    )}
                  </div>
                  {/* Detalhe por unidade só quando há mais de uma — senão repete o de cima. */}
                  {!unicaUnidade && comMelhor.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-central-borda space-y-1">
                      {fechamentos.map((f) => (
                        <div key={f.un.chave} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-400">{f.un.rotulo}{f.un.ehGrupo ? ` · ${f.un.membros.length} itens` : ''}</span>
                          <span className="text-slate-500">Σ piso R$ {fmt(f.piso)}</span>
                          {f.temMelhor ? (
                            <span className={f.cobre ? 'text-emerald-400' : 'text-amber-400'}>
                              {f.cobre ? `✓ cobre (melhor R$ ${fmt(f.melhor)})` : `⚠ acima (melhor R$ ${fmt(f.melhor)})`}
                            </span>
                          ) : (
                            <span className="text-slate-600">sem melhor coletado</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            <p className="text-[11px] text-slate-500 mt-1">O robô nunca lança abaixo do piso. Mudar com a disputa ativa vale na hora (próxima decisão).</p>
          </div>

          {/* Bid Configuration */}
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-3">Configuração de Lances</h4>
            <div className="grid grid-cols-2 gap-4">
              {isBLL ? (
                <div className="col-span-2 text-xs text-slate-400 bg-central-cartao border border-central-borda rounded-lg px-3 py-2">
                  ⏱️ No BLL o robô lança <span className="text-slate-300">reativo por posição</span> (cobre o líder
                  enquanto lucrativo, em Disputa e Prorrogação Automática). Não usa "minutos antes do fim" — na
                  prorrogação o cronômetro reseta a cada lance, então não há fim fixo pra mirar.
                </div>
              ) : cfgIsDispensa ? (
                // Dispensa: não tem fase aberta/timer de início — o robô lança desde
                // o começo e o timing fino fica por conta do "Lance nos últimos
                // segundos" (abaixo). "Início dos lances" só confunde aqui.
                <div className="col-span-2 text-xs text-slate-400 bg-central-cartao border border-central-borda rounded-lg px-3 py-2">
                  ⏱️ Em Dispensa o robô lança <span className="text-slate-300">desde o início</span>; pra atrasar até o
                  fim, use <span className="text-slate-300">"Lance nos últimos segundos"</span> abaixo.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Início dos lances</label>
                    <select
                      value={bidConfig.startMode}
                      onChange={(e) => setBidConfig({ ...bidConfig, startMode: e.target.value as any })}
                      className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                    >
                      <option value="aberto">Desde o início (aberto)</option>
                      <option value="timer">Timer (minutos antes do fim)</option>
                    </select>
                  </div>
                  {bidConfig.startMode === 'timer' && (
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Minutos antes do fim</label>
                      <input
                        type="number"
                        value={bidConfig.timerStartMinutes}
                        onChange={(e) => setBidConfig({ ...bidConfig, timerStartMinutes: Number(e.target.value) })}
                        className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                        min={1} max={60}
                      />
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Casas decimais</label>
                <input
                  type="number"
                  value={bidConfig.decimalPlaces}
                  onChange={(e) => setBidConfig({ ...bidConfig, decimalPlaces: Number(e.target.value) })}
                  className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                  min={0} max={4}
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Tipo de variação</label>
                <select
                  value={bidConfig.reductionType}
                  onChange={(e) => setBidConfig({ ...bidConfig, reductionType: e.target.value as any })}
                  className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                >
                  <option value="percentage">Percentual (%)</option>
                  <option value="fixed">Valor fixo (R$)</option>
                </select>
              </div>
              <div>
                {/*
                  Rótulo diz "entre lances" porque quem manda nela é o PORTAL —
                  BidMargin na sala BLL, variacaoMinimaEntreLances no
                  ComprasNet. O calcBidValue já prefere o valor lido do portal
                  ao daqui; este campo é o fallback de quando não conseguimos
                  ler. Era a única das duas que fazia efeito (todo lance sai
                  pelo mínimo), e agora é a única exibida.
                */}
                <label className="block text-xs text-slate-400 mb-1">
                  Variação mínima entre lances
                  {portalVariacao.min != null && (
                    <span className="ml-1 text-slate-500 font-normal">· portal: {portalVariacao.min}</span>
                  )}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={bidConfig.reductionMin}
                  onChange={(e) => setBidConfig({ ...bidConfig, reductionMin: Number(e.target.value) })}
                  className={`w-full bg-slate-700 border rounded-lg px-3 py-2 text-sm text-white ${
                    portalVariacao.min != null && Number(bidConfig.reductionMin) < portalVariacao.min
                      ? 'border-red-500'
                      : portalVariacao.min != null && Number(bidConfig.reductionMin) === portalVariacao.min
                        ? 'border-amber-600'
                        : 'border-central-borda'
                  }`}
                />
                {portalVariacao.min != null && (() => {
                  const v = Number(bidConfig.reductionMin);
                  const piso = portalVariacao.min;
                  const unit = portalVariacao.tipo === 'V' ? 'R$' : '%';
                  if (v < piso) return (
                    <div className="text-[11px] mt-1 text-red-400">
                      ⛔ Abaixo do piso do portal ({piso}{unit}) — lance será recusado
                    </div>
                  );
                  if (v === piso) return (
                    <div className="text-[11px] mt-1 text-amber-300">
                      ⚠ Igual ao piso do portal ({piso}{unit}). Sugestão: <strong>{(piso * 1.05).toFixed(2)}{unit}</strong> pra ter margem de arredondamento
                    </div>
                  );
                  return null;
                })()}
              </div>
              {/* PCP: janela de desempate da LC 123 (ME/EPP). O robô cobre o melhor
                  lance por 1 intervalo mesmo ABAIXO do piso, até este teto. É valor
                  GLOBAL do lote (o PCP disputa o lote por um valor só). Vazio/null =
                  respeita o piso. Gravado no bidConfig (jsonb aceita campos extras). */}
              {isPcpGlobal && (
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">
                    Desempate ME/EPP — pode ir até (R$, valor global do lote)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    placeholder="vazio = respeita o piso"
                    value={bidConfig.desempateValorMinimo ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBidConfig({ ...bidConfig, desempateValorMinimo: v === '' ? null : Number(v) });
                    }}
                    className={`w-full rounded-lg px-3 py-2 text-sm ${campo}`}
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Na janela de desempate da LC 123 o robô cobre o melhor lance por 1 intervalo mesmo abaixo do piso, até este valor. Vazio = respeita o piso.
                  </div>
                </div>
              )}
              {/*
                "Variação máxima" foi REMOVIDA daqui (20/08/2026). Medido: ela
                não participava de decisão nenhuma. No backend (gkz, ComprasNet,
                PCP) `reductionMax` nunca é lido pra calcular lance; na extensão
                é calculado e descartado na linha seguinte —
                `const max = Number(bidConfig?.reductionMax) || min;` e então
                `return bb * (1 - min / 100)`, com o comentário admitindo que o
                randomized(min, max) ficou pra depois e nunca veio.

                Todo lance sai pelo MÍNIMO. Pior que inútil: o alerta "sem
                espaço estratégico" mandava o operador ajustar, no meio da
                disputa, uma alavanca que não existe.

                O campo segue no bidConfig (sessões antigas e o default do
                banco), só não é mais oferecido. Quem manda na variação é o
                PORTAL — BidMargin no BLL, variacaoMinima no ComprasNet — e o
                calcBidValue já prefere o valor do portal ao configurado aqui.
              */}
            </div>

            {/* Last seconds timing — R1.11: comportamento depende da modalidade+modo */}
            {(() => {
              const part = session?.rankingData?.participacao;
              // F-4 (Onda 2): resolveModalidade (nome > id cru) — um Pregão-PNCP
              // id 6 aparecia aqui como Dispensa e oferecia sniping "padrão ✓".
              const modal = resolveModalidade(session?.item, part?.modalidade);
              const isPregao = modal === 'pregao';
              const isDispensa = modal === 'dispensa';
              // Sniping faz sentido em: Dispensa modo Comum (D), Dispensa modo Aberto (A) — janela fixa sem prorrogação
              // NÃO faz sentido em: Pregão modo Aberto (A) — prorroga 2 min a cada lance no fim → loop infinito
              // NÃO faz sentido em: Pregão modo Aberto-Fechado (F) — fase fechada é envelope único, sem ciclo
              const snipingMakesSense = isDispensa;
              // Em PREGÃO o sniping não funciona (prorroga 2min a cada lance no fim)
              // → escondemos a flag inteira (antes aparecia desabilitada com ⛔).
              if (isPregao) return null;

              return (
                <div className="mt-4">
                  <ToggleOption
                    label="Lance nos últimos segundos"
                    description={
                      snipingMakesSense
                        ? '✓ Em Dispensa a contagem não prorroga — sniping nos últimos segundos é a estratégia padrão.'
                        : 'Enviar lance restando X segundos antes do encerramento (usa timer do portal)'
                    }
                    value={bidConfig.dispensaLastSeconds}
                    onChange={(v) => setBidConfig({ ...bidConfig, dispensaLastSeconds: v })}
                  />
                  {bidConfig.dispensaLastSeconds && (
                    <div className="mt-2 ml-12">
                      <label className="block text-xs text-slate-400 mb-1">Segundos antes do fim</label>
                      <input
                        type="number"
                        value={bidConfig.dispensaLastSecondsValue}
                        onChange={(e) => setBidConfig({ ...bidConfig, dispensaLastSecondsValue: Number(e.target.value) })}
                        className={`w-40 rounded-lg px-3 py-2 text-sm ${campo}`}
                        min={5} max={300}
                      />
                      <div className="text-[11px] text-slate-500 mt-1">
                        Sugestão: <strong>30–60s</strong> (curto suficiente pra concorrentes não cobrirem, longo o bastante pra dar tempo de enviar)
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-central-borda sticky bottom-0 bg-central-cartao">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Cancelar</button>
          <button onClick={handleSave} className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm">
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

// ========== Toggle Option Component ==========

const ToggleOption: React.FC<{
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, description, value, onChange }) => (
  <div className="flex items-start gap-3">
    <button
      onClick={() => onChange(!value)}
      className={`mt-0.5 w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
        value ? 'bg-blue-600' : 'bg-slate-600'
      }`}
    >
      <div className={`w-4 h-4 rounded-full bg-white transition-transform mx-0.5 ${
        value ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
    <div>
      <p className="text-sm text-white">{label}</p>
      <p className="text-xs text-slate-400">{description}</p>
    </div>
  </div>
);

export default DisputeView;
